import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import './i18n';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './components/LanguageSelector';
import UserMenu from './components/UserMenu';
import PasswordChange from './pages/PasswordChange';
import ItemLimits from './components/ItemLimits';

const API_BASE = process.env.REACT_APP_API_BASE ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5000' : '');

export function apiFetch(path, options = {}) {
  return fetch(`${API_BASE}${path}`, options);
}

function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64).split('').map(c => '%' + (`00${c.charCodeAt(0).toString(16)}`).slice(-2)).join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function readSessionFromToken() {
  const token = localStorage.getItem('token');
  const payload = token ? parseJwt(token) : null;
  if (!payload) return null;
  return {
    username: payload.username || '',
    companyId: payload.company_id || '',
    side: payload.side === 'sender' || payload.side === 'receiver' ? payload.side : 'receiver',
    adminRole: payload.admin_role === true,
  };
}

const ADMIN_CONTEXT_KEY = 'dashboardAdminContext';

function readAdminContext() {
  try {
    const raw = localStorage.getItem(ADMIN_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      companyId: parsed?.companyId ? String(parsed.companyId) : '',
      side: parsed?.side === 'sender' || parsed?.side === 'receiver' ? parsed.side : '',
    };
  } catch {
    return null;
  }
}

function writeAdminContext(context) {
  localStorage.setItem(ADMIN_CONTEXT_KEY, JSON.stringify(context));
}

function clearAdminContext() {
  localStorage.removeItem(ADMIN_CONTEXT_KEY);
}

const emptyAdminUserForm = {
  id: null,
  username: '',
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  side: 'receiver',
  is_admin: false,
};

const emptyAdminCompanyForm = {
  id: null,
  company_id: '',
  name: '',
  email: '',
  phone_number: '',
  address: '',
};

const emptyAdminConnectionForm = {
  original_user_id: null,
  original_company_id: '',
  user_id: '',
  company_id: '',
};

const emptyAdminOnboardForm = {
  company: {
    company_id: '',
    name: '',
    email: '',
    phone_number: '',
    address: '',
  },
  user: {
    username: '',
    email: '',
    password: '',
    api_dash: false,
    api_password: '',
    dash_password: '',
    first_name: '',
    last_name: '',
    side: 'receiver',
    is_admin: false,
  },
};

function generatePassword(length = 14) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
  const array = new Uint32Array(length);
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(array);
  }
  return Array.from(array, (value, index) => chars[(value || (Date.now() + index)) % chars.length]).join('');
}

async function copyTextToClipboard(text) {
  if (!text) return false;
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const ok = document.execCommand('copy');
  textarea.remove();
  return ok;
}

function makeAuthKey(username, password) {
  if (!username || !password) return '';
  const raw = `${username}:${password}`;
  return window.btoa(unescape(encodeURIComponent(raw)));
}

// Truncate to `n` chars and add … if longer
function truncate(text, n = 30) {
  if (!text) return '';
  const s = String(text);
  return s.length > n ? s.slice(0, n) + '…' : s;
}

// Format MySQL DATETIME to a friendly string
function formatDate(dt) {
  if (!dt) return '';
  const safe = String(dt).replace(' ', 'T');
  const d = new Date(safe);
  if (isNaN(d)) return String(dt);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* =======================
   Home (public landing)
======================= */
const Home = ({ goDashboard, goLogin }) => {
  const { t } = useTranslation();
  return (
    <div className="page home-shell">
      <section className="home-hero">
        <div className="home-hero-content">
          <div className="home-badge">{t('home_badge') || 'Order Exchange Platform'}</div>
          <h1>{t('home_title') || 'Move orders between stores and distributors in minutes.'}</h1>
          <p className="home-subtitle">
            {t('home_subtitle') || 'One API, one dashboard, one shared truth. Track orders, partners, and line items with clear visibility for both sender and receiver.'}
          </p>
          <div className="cta-row">
            <button className="btn" onClick={goDashboard}>{t('home_cta_dashboard') || 'Open Dashboard'}</button>
            <button className="btn ghost" onClick={goLogin}>{t('home_cta_login') || 'Sign in'}</button>
          </div>

          <div className="home-stats">
            <div className="stat-card">
              <div className="stat-title">{t('home_stat_1_title') || 'Unified Stack'}</div>
              <div className="stat-value">{t('diag_desc_main') || 'API, Database, Dashboard'}</div>
            </div>
            <div className="stat-card">
              <div className="stat-title">{t('home_stat_2_title') || 'Fast Onboarding'}</div>
              <div className="stat-value">{t('diag_easy_onboard') || '• Easy integration'}</div>
            </div>
            <div className="stat-card">
              <div className="stat-title">{t('home_stat_3_title') || 'Security'}</div>
              <div className="stat-value">{t('diag_data_security') || '• Data is secured'}</div>
            </div>
          </div>
        </div>

        <div className="home-hero-panel">
          <div className="flow-card">
            <div className="flow-step">
              <div className="flow-index">01</div>
              <div>
                <div className="flow-title">{t('diag_sender') || 'Sender'}</div>
                <div className="flow-desc">{t('home_flow_1') || 'Creates order with products and delivery date.'}</div>
              </div>
            </div>
            <div className="flow-step">
              <div className="flow-index">02</div>
              <div>
                <div className="flow-title">GetOrders</div>
                <div className="flow-desc">{t('diag_universal_link') || '• Universal API link'}</div>
              </div>
            </div>
            <div className="flow-step">
              <div className="flow-index">03</div>
              <div>
                <div className="flow-title">{t('diag_reciever') || 'Receiver'}</div>
                <div className="flow-desc">{t('home_flow_3') || 'Reviews items and tracks processing.'}</div>
              </div>
            </div>
          </div>

          <div className="signal-card">
            <div className="signal-header">{t('home_signal_title') || 'Operational Snapshot'}</div>
            <div className="signal-grid">
              <div className="signal-item">
                <span className="signal-dot"></span>
                <div>
                  <div className="signal-label">{t('home_signal_1_title') || 'Orders moving'}</div>
                  <div className="signal-value">{t('home_signal_1_value') || 'Live visibility'}</div>
                </div>
              </div>
              <div className="signal-item">
                <span className="signal-dot warn"></span>
                <div>
                  <div className="signal-label">{t('home_signal_2_title') || 'Exceptions'}</div>
                  <div className="signal-value">{t('home_signal_2_value') || 'Cancelled & delayed flagged'}</div>
                </div>
              </div>
              <div className="signal-item">
                <span className="signal-dot ok"></span>
                <div>
                  <div className="signal-label">{t('home_signal_3_title') || 'Exports'}</div>
                  <div className="signal-value">{t('home_signal_3_value') || 'CSV / XLSX / PDF ready'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="home-section">
        <div className="section-header">
          <h2>{t('home_section_title') || 'Built for daily operations'}</h2>
          <p>{t('home_section_subtitle') || 'Filter, drill into order details, and export everything for reconciliation.'}</p>
        </div>
        <div className="home-grid">
          <div className="home-card">
            <div className="card-title">{t('home_card_1_title') || 'Smart Filters'}</div>
            <div className="card-text">{t('home_card_1_text') || 'Date ranges, sender/receiver side, and partner filters in one place.'}</div>
          </div>
          <div className="home-card">
            <div className="card-title">{t('home_card_2_title') || 'Order View'}</div>
            <div className="card-text">{t('home_card_2_text') || 'Inspect headers and line items with a clean, fast modal.'}</div>
          </div>
          <div className="home-card">
            <div className="card-title">{t('home_card_3_title') || 'Exports'}</div>
            <div className="card-text">{t('home_card_3_text') || 'Download CSV, XLSX, or PDF for audit and finance teams.'}</div>
          </div>
        </div>
      </section>

      <section className="home-section dark">
        <div className="benefits">
          <div className="benefits-left">
            <h2>{t('home_benefits_title') || 'Why teams stay on GetOrders'}</h2>
            <p>{t('home_benefits_subtitle') || 'Less manual chasing, more confidence in every shipment.'}</p>
          </div>
          <ul className="benefits-list">
            <li>{t('diag_easy_connection') || '• Simple pairing for Sender/Receiver'}</li>
            <li>{t('diag_no_client_service') || '• No API required on client side'}</li>
            <li>{t('diag_dashboard') || '• Orders monitoring dashboard'}</li>
            <li>{t('diag_data_backup') || '• Automatic backups'}</li>
          </ul>
        </div>
      </section>
    </div>
  );
};
/* =======================
   Login
======================= */
const LoginForm = ({ onLoginSuccess }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');

  const handleLogin = async () => {
    setMessage('');
    try {
      const response = await apiFetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();

      if (data.success) {
        localStorage.setItem('token', data.token);
        onLoginSuccess();
      } else {
        setMessage(data.message);
      }
    } catch (error) {
      setMessage('An error occurred. Could not connect to the server.');
    }
  };

  return (
    <div className="page">
      <div className="login-form" style={{ margin: '40px auto' }}>
        <h1>{t('login_header')}</h1>
        <label>{t('username_label')}</label>
        <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} />
        <label>{t('password_label')}</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        <button onClick={handleLogin}>{t('login_button')}</button>
        {message && <p className="message">{message}</p>}
      </div>
    </div>
  );
};

const PartnerShowcase = ({ compact = false }) => {
  const { t } = useTranslation();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadPartners() {
      try {
        setLoading(true);
        setLoadError('');
        const response = await apiFetch('/api/public/partner-showcase');
        if (!response.ok) throw new Error('Failed to load partner showcase');
        const data = await response.json();
        if (!cancelled) {
          setPartners(Array.isArray(data) ? data : []);
        }
      } catch (_error) {
        if (!cancelled) {
          setPartners([]);
          setLoadError(t('partners_load_error') || 'Failed to load partners.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadPartners();
    return () => {
      cancelled = true;
    };
  }, [t]);

  if (loading) return null;

  if (compact && !partners.length) {
    return null;
  }

  return (
    <>
      <div className="partners-header">
        <hr />
        <span>{t('partners_title') || 'Our Partners'}</span>
        <hr />
      </div>

      {loadError ? (
        <div className="partners-empty">{loadError}</div>
      ) : null}

      {!loadError && !partners.length ? (
        <div className="partners-empty">
          {t('partners_empty') || 'Partners will appear here soon.'}
        </div>
      ) : null}

      {!!partners.length && (
        <div className="partners-grid">
          {partners.map((partner) => {
            const content = (
              <>
                {partner.logo_url ? (
                  <img src={partner.logo_url} alt={partner.name} className="partner-logo" />
                ) : (
                  <span className="partner-name">{partner.name}</span>
                )}
              </>
            );

            if (partner.website_url) {
              return (
                <a
                  key={partner.id}
                  href={partner.website_url}
                  className="partner-card"
                  target="_blank"
                  rel="noreferrer"
                >
                  {content}
                </a>
              );
            }

            return (
              <div className="partner-card" key={partner.id}>
                {content}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

/* =======================
   Dashboard
======================= */
const Dashboard = ({
  onLogout,
  sessionCompanyId = '',
  sessionSide = 'receiver',
  adminRole = false,
  adminContext = null,
  onAdminContextChange,
}) => {
  const { t } = useTranslation();
  const effectiveCompanyId = adminRole ? (adminContext?.companyId || sessionCompanyId) : sessionCompanyId;
  const effectiveSide = adminRole ? (adminContext?.side || sessionSide) : sessionSide;
  const canManageItems = adminRole || effectiveSide === 'sender';
  const canManageObjects = adminRole || effectiveSide === 'sender';
  const canManageItemLimits = adminRole || effectiveSide === 'sender';
  const [activeTab, setActiveTab] = useState('orders');
  const [adminPanelTab, setAdminPanelTab] = useState('session');
  const [adminCompanies, setAdminCompanies] = useState([]);
  const [adminCompaniesBusy, setAdminCompaniesBusy] = useState(false);
  const [adminPanelError, setAdminPanelError] = useState('');
  const [adminDraftCompanyId, setAdminDraftCompanyId] = useState(effectiveCompanyId);
  const [adminDraftSide, setAdminDraftSide] = useState(effectiveSide);
  const [adminManagementTab, setAdminManagementTab] = useState('users');
  const [adminManagementBusy, setAdminManagementBusy] = useState(false);
  const [adminManagementMessage, setAdminManagementMessage] = useState('');
  const [adminManagementError, setAdminManagementError] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminCompaniesFull, setAdminCompaniesFull] = useState([]);
  const [adminConnections, setAdminConnections] = useState([]);
  const [adminUserSearch, setAdminUserSearch] = useState('');
  const [adminCompanySearch, setAdminCompanySearch] = useState('');
  const [adminConnectionSearch, setAdminConnectionSearch] = useState('');
  const [adminUserForm, setAdminUserForm] = useState(emptyAdminUserForm);
  const [adminCompanyForm, setAdminCompanyForm] = useState(emptyAdminCompanyForm);
  const [adminConnectionForm, setAdminConnectionForm] = useState(emptyAdminConnectionForm);
  const [adminOnboardOpen, setAdminOnboardOpen] = useState(false);
  const [adminOnboardForm, setAdminOnboardForm] = useState(emptyAdminOnboardForm);
  const [adminPasswordCopyMessage, setAdminPasswordCopyMessage] = useState('');
  const [orders, setOrders] = useState([]);
  const [message, setMessage] = useState(t('loading_orders'));
  const [itemsFile, setItemsFile] = useState(null);
  const [itemsFeedback, setItemsFeedback] = useState('');
  const [itemsError, setItemsError] = useState('');
  const [itemsBusy, setItemsBusy] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [itemsInputKey, setItemsInputKey] = useState(0);
  const [itemsList, setItemsList] = useState([]);
  const [itemsListBusy, setItemsListBusy] = useState(false);
  const [itemsExportBusy, setItemsExportBusy] = useState(false);
  const [itemsCodeFilter, setItemsCodeFilter] = useState('');
  const [itemsNameFilter, setItemsNameFilter] = useState('');
  const [itemForm, setItemForm] = useState({
    item_code: '',
    item_name: '',
    brand_name: '',
  });
  const [editItemOpen, setEditItemOpen] = useState(false);
  const [editItemForm, setEditItemForm] = useState({
    id: null,
    item_code: '',
    item_name: '',
    brand_name: '',
  });
  const [objectsFile, setObjectsFile] = useState(null);
  const [objectsFeedback, setObjectsFeedback] = useState('');
  const [objectsError, setObjectsError] = useState('');
  const [objectsBusy, setObjectsBusy] = useState(false);
  const [objectsTemplateBusy, setObjectsTemplateBusy] = useState(false);
  const [objectsInputKey, setObjectsInputKey] = useState(0);
  const [objectsList, setObjectsList] = useState([]);
  const [objectsListBusy, setObjectsListBusy] = useState(false);
  const [objectsExportBusy, setObjectsExportBusy] = useState(false);
  const [objectsIdFilter, setObjectsIdFilter] = useState('');
  const [objectsNameFilter, setObjectsNameFilter] = useState('');
  const [objectForm, setObjectForm] = useState({
    object_id: '',
    object_name: '',
    object_address: '',
  });
  const [editObjectOpen, setEditObjectOpen] = useState(false);
  const [editObjectForm, setEditObjectForm] = useState({
    id: null,
    object_id: '',
    object_name: '',
    object_address: '',
  });

  const [openId, setOpenId] = useState(null);     // selected order_id
  const [detail, setDetail] = useState(null);     // { order: {...} }
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailErr, setDetailErr] = useState('');

  async function openOrder(order_id) {
    try {
      setOpenId(order_id);
      setDetail(null);
      setDetailErr('');
      setLoadingDetail(true);
      const token = localStorage.getItem('token');
      const qs = new URLSearchParams({ side });
      const res = await apiFetch(`/api/orders/${encodeURIComponent(order_id)}?${qs.toString()}`, {
        headers: authHeaders(token)
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setDetail(data.order);
    } catch (e) {
      setDetailErr('Failed to load order.');
    } finally {
      setLoadingDetail(false);
    }
  }

  function closeOrder() {
    setOpenId(null);
    setDetail(null);
    setDetailErr('');
  }

  const fmt = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString().slice(0, 10);

  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const today = fmt(now);

  const [beginDate, setBeginDate] = useState(fmt(firstOfMonth));
  const [endDate, setEndDate] = useState(today);
  const [side, setSide] = useState(effectiveSide); // mirrors effective session side
  const [partnerId, setPartnerId] = useState('');
  const [partners, setPartners] = useState([]);
  const [showExport, setShowExport] = useState(false);

  useEffect(() => {
    if (!adminRole) {
      setSide(sessionSide);
      setPartnerId('');
    }
  }, [adminRole, sessionSide]);

  useEffect(() => {
    setSide(effectiveSide);
    setPartnerId('');
    setAdminDraftCompanyId(effectiveCompanyId);
    setAdminDraftSide(effectiveSide);
  }, [effectiveCompanyId, effectiveSide]);

  useEffect(() => {
    if (!canManageItems && activeTab === 'items') {
      setActiveTab('orders');
    }
    if (!canManageObjects && activeTab === 'objects') {
      setActiveTab('orders');
    }
    if (!canManageItemLimits && activeTab === 'item-limits') {
      setActiveTab('orders');
    }
  }, [activeTab, canManageItems, canManageObjects, canManageItemLimits]);

  async function loadAdminCompanies() {
    const token = localStorage.getItem('token');
    if (!token || !adminRole) return;
    try {
      setAdminCompaniesBusy(true);
      setAdminPanelError('');
      const response = await apiFetch('/api/admin/companies', {
        method: 'GET',
        headers: authHeaders(token),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Failed to load companies'));
      }
      const data = await response.json();
      setAdminCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      setAdminCompanies([]);
      setAdminPanelError(error.message || 'Failed to load companies');
    } finally {
      setAdminCompaniesBusy(false);
    }
  }

  useEffect(() => {
    if (adminRole && activeTab === 'admin') {
      loadAdminCompanies();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminRole, activeTab]);

  function applyAdminContext() {
    if (!adminRole || !onAdminContextChange) return;
    onAdminContextChange({
      companyId: adminDraftCompanyId.trim(),
      side: adminDraftSide,
    });
    setAdminPanelError('');
  }

  function resetAdminContext() {
    if (!adminRole || !onAdminContextChange) return;
    onAdminContextChange({
      companyId: sessionCompanyId,
      side: sessionSide,
    });
    setAdminDraftCompanyId(sessionCompanyId);
    setAdminDraftSide(sessionSide);
    setAdminPanelError('');
  }

  const authHeaders = useCallback((token, extra = {}) => {
    const headers = {
      Authorization: `Bearer ${token}`,
      ...extra,
    };
    if (adminRole) {
      headers['X-Session-Company-Id'] = effectiveCompanyId;
      headers['X-Session-Side'] = effectiveSide;
    }
    return headers;
  }, [adminRole, effectiveCompanyId, effectiveSide]);

  const loadOrders = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setMessage(t('no_token_error'));
      return;
    }
    try {
      setMessage(t('loading_orders'));
      const qs = new URLSearchParams();
      if (beginDate) qs.set('begin', beginDate);
      if (endDate) qs.set('end', endDate);
      if (side) qs.set('side', side);
      if (partnerId.trim()) qs.set('partner_id', partnerId.trim());

      const response = await apiFetch(`/api/orders?${qs.toString()}`, {
        method: 'GET',
        headers: authHeaders(token)
      });
      if (!response.ok) throw new Error(t('fetch_orders_error'));
      const data = await response.json();
      setOrders(data);
      setMessage('');
    } catch (error) {
      setMessage(error.message || t('fetch_orders_error'));
      onLogout();
    }
  }, [beginDate, endDate, side, partnerId, authHeaders, onLogout, t]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const exportOrders = useCallback(async (format) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setMessage(t('no_token_error'));
      return;
    }
    try {
      const qs = new URLSearchParams();
      if (beginDate) qs.set('begin', beginDate);
      if (endDate) qs.set('end', endDate);
      if (side) qs.set('side', side);
      if (partnerId.trim()) qs.set('partner_id', partnerId.trim());
      if (format) qs.set('format', format);

      const response = await apiFetch(`/api/orders/export?${qs.toString()}`, {
        method: 'GET',
        headers: authHeaders(token)
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = (format || 'csv').toLowerCase();
      a.download = `orders_${beginDate || 'start'}_${endDate || 'end'}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage(error.message || 'Export failed');
    }
  }, [beginDate, endDate, side, partnerId, authHeaders, t]);

  const loadPartners = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await apiFetch(`/api/partners?side=${encodeURIComponent(side)}`, {
        method: 'GET',
        headers: authHeaders(token)
      });
      if (!response.ok) throw new Error('Failed to load partners');
      const data = await response.json();
      setPartners(Array.isArray(data) ? data : []);
    } catch (_e) {
      setPartners([]);
    }
  }, [side, authHeaders]);

  useEffect(() => {
    setPartnerId('');
    loadPartners();
  }, [side, loadPartners]);

  async function readErrorMessage(response, fallback) {
    try {
      const data = await response.json();
      return data?.error || data?.message || fallback;
    } catch (_e) {
      return fallback;
    }
  }

  async function loadAdminUsers() {
    const token = localStorage.getItem('token');
    if (!token || !adminRole) return;
    try {
      setAdminManagementBusy(true);
      setAdminManagementError('');
      const qs = new URLSearchParams();
      if (adminUserSearch.trim()) qs.set('search', adminUserSearch.trim());
      const response = await apiFetch(`/api/admin/manage/users?${qs.toString()}`, {
        method: 'GET',
        headers: authHeaders(token),
      });
      if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to load users'));
      const data = await response.json();
      setAdminUsers(Array.isArray(data.users) ? data.users : []);
    } catch (error) {
      setAdminUsers([]);
      setAdminManagementError(error.message || 'Failed to load users');
    } finally {
      setAdminManagementBusy(false);
    }
  }

  async function loadAdminCompaniesFull() {
    const token = localStorage.getItem('token');
    if (!token || !adminRole) return;
    try {
      setAdminManagementBusy(true);
      setAdminManagementError('');
      const qs = new URLSearchParams();
      if (adminCompanySearch.trim()) qs.set('search', adminCompanySearch.trim());
      const response = await apiFetch(`/api/admin/manage/companies?${qs.toString()}`, {
        method: 'GET',
        headers: authHeaders(token),
      });
      if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to load companies'));
      const data = await response.json();
      setAdminCompaniesFull(Array.isArray(data.companies) ? data.companies : []);
    } catch (error) {
      setAdminCompaniesFull([]);
      setAdminManagementError(error.message || 'Failed to load companies');
    } finally {
      setAdminManagementBusy(false);
    }
  }

  async function loadAdminConnections() {
    const token = localStorage.getItem('token');
    if (!token || !adminRole) return;
    try {
      setAdminManagementBusy(true);
      setAdminManagementError('');
      const qs = new URLSearchParams();
      if (adminConnectionSearch.trim()) qs.set('search', adminConnectionSearch.trim());
      const response = await apiFetch(`/api/admin/manage/user-companies?${qs.toString()}`, {
        method: 'GET',
        headers: authHeaders(token),
      });
      if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to load user/company connections'));
      const data = await response.json();
      setAdminConnections(Array.isArray(data.user_companies) ? data.user_companies : []);
    } catch (error) {
      setAdminConnections([]);
      setAdminManagementError(error.message || 'Failed to load user/company connections');
    } finally {
      setAdminManagementBusy(false);
    }
  }

  async function reloadAdminManagement() {
    if (!adminRole) return;
    if (adminManagementTab === 'users') {
      await loadAdminUsers();
    } else if (adminManagementTab === 'companies') {
      await Promise.all([loadAdminCompaniesFull(), loadAdminCompanies()]);
    } else {
      await Promise.all([loadAdminConnections(), loadAdminUsers(), loadAdminCompanies()]);
    }
  }

  useEffect(() => {
    if (adminRole && activeTab === 'admin' && adminPanelTab === 'management') {
      reloadAdminManagement();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminRole, activeTab, adminPanelTab, adminManagementTab]);

  function generateAdminUserPassword(target = 'user') {
    const password = generatePassword();
    if (target === 'onboardApi') {
      setAdminOnboardForm((prev) => ({
        ...prev,
        user: { ...prev.user, api_password: password },
      }));
    } else if (target === 'onboardDash') {
      setAdminOnboardForm((prev) => ({
        ...prev,
        user: { ...prev.user, dash_password: password },
      }));
    } else if (target === 'onboard') {
      setAdminOnboardForm((prev) => ({
        ...prev,
        user: { ...prev.user, password },
      }));
    } else {
      setAdminUserForm((prev) => ({ ...prev, password }));
    }
    setAdminPasswordCopyMessage('Password generated.');
  }

  async function copyAdminPassword(password) {
    try {
      const ok = await copyTextToClipboard(password);
      setAdminPasswordCopyMessage(ok ? 'Password copied.' : 'Copy failed.');
    } catch (_e) {
      setAdminPasswordCopyMessage('Copy failed.');
    }
  }

  function startEditAdminUser(user) {
    setAdminUserForm({
      id: user.id,
      username: user.username || '',
      email: user.email || '',
      password: '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      side: user.side === 'sender' ? 'sender' : 'receiver',
      is_admin: !!user.is_admin,
    });
    setAdminManagementMessage('');
    setAdminManagementError('');
  }

  function startEditAdminCompany(company) {
    setAdminCompanyForm({
      id: company.id,
      company_id: company.company_id || '',
      name: company.name || '',
      email: company.email || '',
      phone_number: company.phone_number || '',
      address: company.address || '',
    });
    setAdminManagementMessage('');
    setAdminManagementError('');
  }

  function startEditAdminConnection(connection) {
    setAdminConnectionForm({
      original_user_id: connection.user_id,
      original_company_id: connection.company_id,
      user_id: String(connection.user_id || ''),
      company_id: connection.company_id || '',
    });
    setAdminManagementMessage('');
    setAdminManagementError('');
  }

  async function saveAdminUser() {
    const token = localStorage.getItem('token');
    if (!token) return;
    const editing = !!adminUserForm.id;
    if (!adminUserForm.username.trim() || !adminUserForm.email.trim() || !adminUserForm.side) {
      setAdminManagementError('Username, email and side are required.');
      return;
    }
    if (!editing && !adminUserForm.password.trim()) {
      setAdminManagementError('Password is required for a new user.');
      return;
    }
    try {
      setAdminManagementBusy(true);
      setAdminManagementError('');
      setAdminManagementMessage('');
      const response = await apiFetch(
        editing ? `/api/admin/manage/users/${adminUserForm.id}` : '/api/admin/manage/users',
        {
          method: editing ? 'PUT' : 'POST',
          headers: authHeaders(token, { 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            username: adminUserForm.username.trim(),
            email: adminUserForm.email.trim(),
            password: adminUserForm.password.trim(),
            first_name: adminUserForm.first_name.trim(),
            last_name: adminUserForm.last_name.trim(),
            side: adminUserForm.side,
            is_admin: adminUserForm.is_admin,
          }),
        }
      );
      if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to save user'));
      setAdminManagementMessage(editing ? 'User updated.' : 'User created.');
      setAdminUserForm(emptyAdminUserForm);
      await loadAdminUsers();
    } catch (error) {
      setAdminManagementError(error.message || 'Failed to save user');
    } finally {
      setAdminManagementBusy(false);
    }
  }

  async function deleteAdminUser(user) {
    const token = localStorage.getItem('token');
    if (!token || !window.confirm(`Delete user ${user.username}?`)) return;
    try {
      setAdminManagementBusy(true);
      setAdminManagementError('');
      const response = await apiFetch(`/api/admin/manage/users/${user.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to delete user'));
      setAdminManagementMessage('User deleted.');
      await loadAdminUsers();
    } catch (error) {
      setAdminManagementError(error.message || 'Failed to delete user');
    } finally {
      setAdminManagementBusy(false);
    }
  }

  async function saveAdminCompany() {
    const token = localStorage.getItem('token');
    if (!token) return;
    const editing = !!adminCompanyForm.id;
    if (!adminCompanyForm.company_id.trim()) {
      setAdminManagementError('Company ID is required.');
      return;
    }
    try {
      setAdminManagementBusy(true);
      setAdminManagementError('');
      setAdminManagementMessage('');
      const response = await apiFetch(
        editing ? `/api/admin/manage/companies/${adminCompanyForm.id}` : '/api/admin/manage/companies',
        {
          method: editing ? 'PUT' : 'POST',
          headers: authHeaders(token, { 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            company_id: adminCompanyForm.company_id.trim(),
            name: adminCompanyForm.name.trim(),
            email: adminCompanyForm.email.trim(),
            phone_number: adminCompanyForm.phone_number.trim(),
            address: adminCompanyForm.address.trim(),
          }),
        }
      );
      if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to save company'));
      setAdminManagementMessage(editing ? 'Company updated.' : 'Company created.');
      setAdminCompanyForm(emptyAdminCompanyForm);
      await loadAdminCompaniesFull();
      await loadAdminCompanies();
    } catch (error) {
      setAdminManagementError(error.message || 'Failed to save company');
    } finally {
      setAdminManagementBusy(false);
    }
  }

  async function deleteAdminCompany(company) {
    const token = localStorage.getItem('token');
    if (!token || !window.confirm(`Delete company ${company.company_id}?`)) return;
    try {
      setAdminManagementBusy(true);
      setAdminManagementError('');
      const response = await apiFetch(`/api/admin/manage/companies/${company.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to delete company'));
      setAdminManagementMessage('Company deleted.');
      await loadAdminCompaniesFull();
      await loadAdminCompanies();
    } catch (error) {
      setAdminManagementError(error.message || 'Failed to delete company');
    } finally {
      setAdminManagementBusy(false);
    }
  }

  async function saveAdminConnection() {
    const token = localStorage.getItem('token');
    if (!token) return;
    const editing = !!adminConnectionForm.original_user_id && !!adminConnectionForm.original_company_id;
    const userId = Number(adminConnectionForm.user_id);
    if (!Number.isInteger(userId) || userId <= 0 || !adminConnectionForm.company_id.trim()) {
      setAdminManagementError('User ID and company ID are required.');
      return;
    }
    try {
      setAdminManagementBusy(true);
      setAdminManagementError('');
      setAdminManagementMessage('');
      const path = editing
        ? `/api/admin/manage/user-companies/${adminConnectionForm.original_user_id}/${encodeURIComponent(adminConnectionForm.original_company_id)}`
        : '/api/admin/manage/user-companies';
      const response = await apiFetch(path, {
        method: editing ? 'PUT' : 'POST',
        headers: authHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          user_id: userId,
          company_id: adminConnectionForm.company_id.trim(),
        }),
      });
      if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to save connection'));
      setAdminManagementMessage(editing ? 'Connection updated.' : 'Connection created.');
      setAdminConnectionForm(emptyAdminConnectionForm);
      await loadAdminConnections();
    } catch (error) {
      setAdminManagementError(error.message || 'Failed to save connection');
    } finally {
      setAdminManagementBusy(false);
    }
  }

  async function deleteAdminConnection(connection) {
    const token = localStorage.getItem('token');
    if (!token || !window.confirm(`Delete connection ${connection.user_id} / ${connection.company_id}?`)) return;
    try {
      setAdminManagementBusy(true);
      setAdminManagementError('');
      const response = await apiFetch(
        `/api/admin/manage/user-companies/${connection.user_id}/${encodeURIComponent(connection.company_id)}`,
        { method: 'DELETE', headers: authHeaders(token) }
      );
      if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to delete connection'));
      setAdminManagementMessage('Connection deleted.');
      await loadAdminConnections();
    } catch (error) {
      setAdminManagementError(error.message || 'Failed to delete connection');
    } finally {
      setAdminManagementBusy(false);
    }
  }

  async function createAdminOnboarding() {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (
      !adminOnboardForm.company.company_id.trim() ||
      !adminOnboardForm.user.username.trim() ||
      !adminOnboardForm.user.email.trim()
    ) {
      setAdminManagementError('Company ID, username and email are required.');
      return;
    }
    if (!adminOnboardForm.user.api_dash && !adminOnboardForm.user.password.trim()) {
      setAdminManagementError('Password is required.');
      return;
    }
    if (
      adminOnboardForm.user.api_dash &&
      (!adminOnboardForm.user.api_password.trim() || !adminOnboardForm.user.dash_password.trim())
    ) {
      setAdminManagementError('API password and Dash password are required.');
      return;
    }
    try {
      setAdminManagementBusy(true);
      setAdminManagementError('');
      setAdminManagementMessage('');
      const response = await apiFetch('/api/admin/manage/onboard', {
        method: 'POST',
        headers: authHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify(adminOnboardForm),
      });
      if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to create company and user'));
      setAdminManagementMessage('Company, user and connection created.');
      setAdminOnboardForm(emptyAdminOnboardForm);
      setAdminOnboardOpen(false);
      await Promise.all([loadAdminCompanies(), loadAdminUsers(), loadAdminCompaniesFull(), loadAdminConnections()]);
    } catch (error) {
      setAdminManagementError(error.message || 'Failed to create company and user');
    } finally {
      setAdminManagementBusy(false);
    }
  }

  async function downloadItemsTemplate() {
    const token = localStorage.getItem('token');
    if (!token) {
      setItemsError(t('no_token_error'));
      return;
    }

    try {
      setItemsError('');
      setItemsFeedback('');
      setTemplateBusy(true);

      const response = await apiFetch('/api/items/template', {
        method: 'GET',
        headers: authHeaders(token),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('items_template_failed') || 'Template download failed'));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'items_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setItemsFeedback(t('items_template_success') || 'Template downloaded.');
    } catch (error) {
      setItemsError(error.message || t('items_template_failed') || 'Template download failed');
    } finally {
      setTemplateBusy(false);
    }
  }

  async function loadItems() {
    const token = localStorage.getItem('token');
    if (!token) {
      setItemsError(t('no_token_error'));
      return;
    }
    if (!canManageItems) {
      setItemsError(t('items_load_failed') || 'Failed to load items');
      return;
    }

    try {
      setItemsListBusy(true);
      setItemsError('');
      const qs = new URLSearchParams();
      if (itemsCodeFilter.trim()) qs.set('item_code', itemsCodeFilter.trim());
      if (itemsNameFilter.trim()) qs.set('item_name', itemsNameFilter.trim());

      const response = await apiFetch(`/api/items?${qs.toString()}`, {
        method: 'GET',
        headers: authHeaders(token),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('items_load_failed') || 'Failed to load items'));
      }

      const data = await response.json();
      setItemsList(Array.isArray(data.items) ? data.items : []);
    } catch (error) {
      setItemsError(error.message || t('items_load_failed') || 'Failed to load items');
    } finally {
      setItemsListBusy(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'items') {
      loadItems();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, effectiveCompanyId, effectiveSide]);

  useEffect(() => {
    if (activeTab === 'objects') {
      loadObjects();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, effectiveCompanyId, effectiveSide]);

  function resetItemForm() {
    setItemForm({
      item_code: '',
      item_name: '',
      brand_name: '',
    });
  }

  function startEditItem(item) {
    setEditItemForm({
      id: item.id,
      item_code: item.item_code || '',
      item_name: item.item_name || '',
      brand_name: item.brand_name || '',
    });
    setEditItemOpen(true);
    setItemsError('');
    setItemsFeedback('');
  }

  function closeEditItem() {
    setEditItemOpen(false);
    setEditItemForm({
      id: null,
      item_code: '',
      item_name: '',
      brand_name: '',
    });
  }

  async function downloadItemsExport() {
    const token = localStorage.getItem('token');
    if (!token) {
      setItemsError(t('no_token_error'));
      return;
    }

    try {
      setItemsExportBusy(true);
      setItemsError('');
      setItemsFeedback('');
      const response = await apiFetch('/api/items/export', {
        method: 'GET',
        headers: authHeaders(token),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('items_export_failed') || 'Items export failed'));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'items.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setItemsFeedback(t('items_export_success') || 'Items file downloaded.');
    } catch (error) {
      setItemsError(error.message || t('items_export_failed') || 'Items export failed');
    } finally {
      setItemsExportBusy(false);
    }
  }

  async function uploadItemsFile() {
    const token = localStorage.getItem('token');
    if (!token) {
      setItemsError(t('no_token_error'));
      return;
    }
    if (!itemsFile) {
      setItemsError(t('items_file_required') || 'Please choose an Excel file.');
      return;
    }

    try {
      setItemsBusy(true);
      setItemsError('');
      setItemsFeedback('');

      const form = new FormData();
      form.append('file', itemsFile);

      const response = await apiFetch('/api/items/upload', {
        method: 'POST',
        headers: authHeaders(token),
        body: form,
      });

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('items_upload_failed') || 'Items upload failed'));
      }

      const data = await response.json();
      setItemsFeedback(
        t('items_upload_success', { count: data.uploaded }) ||
        `Uploaded ${data.uploaded} item rows.`
      );
      setItemsFile(null);
      setItemsInputKey((value) => value + 1);
      await loadItems();
    } catch (error) {
      setItemsError(error.message || t('items_upload_failed') || 'Items upload failed');
    } finally {
      setItemsBusy(false);
    }
  }

  async function saveItem() {
    const token = localStorage.getItem('token');
    if (!token) {
      setItemsError(t('no_token_error'));
      return;
    }
    if (!itemForm.item_code.trim() || !itemForm.item_name.trim()) {
      setItemsError(t('items_form_required') || 'Item code and item name are required.');
      return;
    }

    try {
      setItemsBusy(true);
      setItemsError('');
      setItemsFeedback('');
      const response = await apiFetch('/api/items', {
        method: 'POST',
        headers: {
          ...authHeaders(token, { 'Content-Type': 'application/json' }),
        },
        body: JSON.stringify({
          item_code: itemForm.item_code.trim(),
          item_name: itemForm.item_name.trim(),
          brand_name: itemForm.brand_name.trim(),
        }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('items_save_failed') || 'Failed to save item'));
      }

      await response.json();
      setItemsFeedback(t('items_create_success') || 'Item added.');
      resetItemForm();
      await loadItems();
    } catch (error) {
      setItemsError(error.message || t('items_save_failed') || 'Failed to save item');
    } finally {
      setItemsBusy(false);
    }
  }

  async function updateItem() {
    const token = localStorage.getItem('token');
    if (!token) {
      setItemsError(t('no_token_error'));
      return;
    }
    if (!editItemForm.id || !editItemForm.item_code.trim() || !editItemForm.item_name.trim()) {
      setItemsError(t('items_form_required') || 'Item code and item name are required.');
      return;
    }

    try {
      setItemsBusy(true);
      setItemsError('');
      setItemsFeedback('');
      const response = await apiFetch(`/api/items/${editItemForm.id}`, {
        method: 'PUT',
        headers: {
          ...authHeaders(token, { 'Content-Type': 'application/json' }),
        },
        body: JSON.stringify({
          item_code: editItemForm.item_code.trim(),
          item_name: editItemForm.item_name.trim(),
          brand_name: editItemForm.brand_name.trim(),
        }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('items_save_failed') || 'Failed to save item'));
      }

      await response.json();
      setItemsFeedback(t('items_update_success') || 'Item updated.');
      closeEditItem();
      await loadItems();
    } catch (error) {
      setItemsError(error.message || t('items_save_failed') || 'Failed to save item');
    } finally {
      setItemsBusy(false);
    }
  }

  async function deleteItem(item) {
    const token = localStorage.getItem('token');
    if (!token) {
      setItemsError(t('no_token_error'));
      return;
    }
    if (!window.confirm(t('items_delete_confirm') || 'Delete this item?')) {
      return;
    }

    try {
      setItemsBusy(true);
      setItemsError('');
      setItemsFeedback('');
      const response = await apiFetch(`/api/items/${item.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('items_delete_failed') || 'Failed to delete item'));
      }

      if (itemForm.id === item.id) resetItemForm();
      setItemsFeedback(t('items_delete_success') || 'Item deleted.');
      await loadItems();
    } catch (error) {
      setItemsError(error.message || t('items_delete_failed') || 'Failed to delete item');
    } finally {
      setItemsBusy(false);
    }
  }

  async function downloadObjectsTemplate() {
    const token = localStorage.getItem('token');
    if (!token) {
      setObjectsError(t('no_token_error'));
      return;
    }

    try {
      setObjectsError('');
      setObjectsFeedback('');
      setObjectsTemplateBusy(true);

      const response = await apiFetch('/api/objects/template', {
        method: 'GET',
        headers: authHeaders(token),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('objects_template_failed') || 'Template download failed'));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'objects_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setObjectsFeedback(t('objects_template_success') || 'Template downloaded.');
    } catch (error) {
      setObjectsError(error.message || t('objects_template_failed') || 'Template download failed');
    } finally {
      setObjectsTemplateBusy(false);
    }
  }

  async function loadObjects() {
    const token = localStorage.getItem('token');
    if (!token) {
      setObjectsError(t('no_token_error'));
      return;
    }
    if (!canManageObjects) {
      setObjectsError(t('objects_load_failed') || 'Failed to load objects');
      return;
    }

    try {
      setObjectsListBusy(true);
      setObjectsError('');
      const qs = new URLSearchParams();
      if (objectsIdFilter.trim()) qs.set('object_id', objectsIdFilter.trim());
      if (objectsNameFilter.trim()) qs.set('object_name', objectsNameFilter.trim());

      const response = await apiFetch(`/api/objects?${qs.toString()}`, {
        method: 'GET',
        headers: authHeaders(token),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('objects_load_failed') || 'Failed to load objects'));
      }

      const data = await response.json();
      setObjectsList(Array.isArray(data.objects) ? data.objects : []);
    } catch (error) {
      setObjectsError(error.message || t('objects_load_failed') || 'Failed to load objects');
    } finally {
      setObjectsListBusy(false);
    }
  }

  function resetObjectForm() {
    setObjectForm({
      object_id: '',
      object_name: '',
      object_address: '',
    });
  }

  function startEditObject(object) {
    setEditObjectForm({
      id: object.id,
      object_id: object.object_id || '',
      object_name: object.object_name || '',
      object_address: object.object_address || '',
    });
    setEditObjectOpen(true);
    setObjectsError('');
    setObjectsFeedback('');
  }

  function closeEditObject() {
    setEditObjectOpen(false);
    setEditObjectForm({
      id: null,
      object_id: '',
      object_name: '',
      object_address: '',
    });
  }

  async function downloadObjectsExport() {
    const token = localStorage.getItem('token');
    if (!token) {
      setObjectsError(t('no_token_error'));
      return;
    }

    try {
      setObjectsExportBusy(true);
      setObjectsError('');
      setObjectsFeedback('');
      const response = await apiFetch('/api/objects/export', {
        method: 'GET',
        headers: authHeaders(token),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('objects_export_failed') || 'Objects export failed'));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'objects.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setObjectsFeedback(t('objects_export_success') || 'Objects file downloaded.');
    } catch (error) {
      setObjectsError(error.message || t('objects_export_failed') || 'Objects export failed');
    } finally {
      setObjectsExportBusy(false);
    }
  }

  async function uploadObjectsFile() {
    const token = localStorage.getItem('token');
    if (!token) {
      setObjectsError(t('no_token_error'));
      return;
    }
    if (!objectsFile) {
      setObjectsError(t('objects_file_required') || 'Please choose an Excel file.');
      return;
    }

    try {
      setObjectsBusy(true);
      setObjectsError('');
      setObjectsFeedback('');

      const form = new FormData();
      form.append('file', objectsFile);

      const response = await apiFetch('/api/objects/upload', {
        method: 'POST',
        headers: authHeaders(token),
        body: form,
      });

      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('objects_upload_failed') || 'Objects upload failed'));
      }

      const data = await response.json();
      setObjectsFeedback(
        t('objects_upload_success', { count: data.uploaded }) ||
        `Uploaded ${data.uploaded} object rows.`
      );
      setObjectsFile(null);
      setObjectsInputKey((value) => value + 1);
      await loadObjects();
    } catch (error) {
      setObjectsError(error.message || t('objects_upload_failed') || 'Objects upload failed');
    } finally {
      setObjectsBusy(false);
    }
  }

  async function saveObject() {
    const token = localStorage.getItem('token');
    if (!token) {
      setObjectsError(t('no_token_error'));
      return;
    }
    if (!objectForm.object_id.trim() || !objectForm.object_name.trim() || !objectForm.object_address.trim()) {
      setObjectsError(t('objects_form_required') || 'Object ID, object name and object address are required.');
      return;
    }

    try {
      setObjectsBusy(true);
      setObjectsError('');
      setObjectsFeedback('');
      const response = await apiFetch('/api/objects', {
        method: 'POST',
        headers: {
          ...authHeaders(token, { 'Content-Type': 'application/json' }),
        },
        body: JSON.stringify({
          object_id: objectForm.object_id.trim(),
          object_name: objectForm.object_name.trim(),
          object_address: objectForm.object_address.trim(),
        }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('objects_save_failed') || 'Failed to save object'));
      }

      await response.json();
      setObjectsFeedback(t('objects_create_success') || 'Object added.');
      resetObjectForm();
      await loadObjects();
    } catch (error) {
      setObjectsError(error.message || t('objects_save_failed') || 'Failed to save object');
    } finally {
      setObjectsBusy(false);
    }
  }

  async function updateObject() {
    const token = localStorage.getItem('token');
    if (!token) {
      setObjectsError(t('no_token_error'));
      return;
    }
    if (
      !editObjectForm.id ||
      !editObjectForm.object_id.trim() ||
      !editObjectForm.object_name.trim() ||
      !editObjectForm.object_address.trim()
    ) {
      setObjectsError(t('objects_form_required') || 'Object ID, object name and object address are required.');
      return;
    }

    try {
      setObjectsBusy(true);
      setObjectsError('');
      setObjectsFeedback('');
      const response = await apiFetch(`/api/objects/${editObjectForm.id}`, {
        method: 'PUT',
        headers: {
          ...authHeaders(token, { 'Content-Type': 'application/json' }),
        },
        body: JSON.stringify({
          object_id: editObjectForm.object_id.trim(),
          object_name: editObjectForm.object_name.trim(),
          object_address: editObjectForm.object_address.trim(),
        }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('objects_save_failed') || 'Failed to save object'));
      }

      await response.json();
      setObjectsFeedback(t('objects_update_success') || 'Object updated.');
      closeEditObject();
      await loadObjects();
    } catch (error) {
      setObjectsError(error.message || t('objects_save_failed') || 'Failed to save object');
    } finally {
      setObjectsBusy(false);
    }
  }

  async function deleteObject(object) {
    const token = localStorage.getItem('token');
    if (!token) {
      setObjectsError(t('no_token_error'));
      return;
    }
    if (!window.confirm(t('objects_delete_confirm') || 'Delete this object?')) {
      return;
    }

    try {
      setObjectsBusy(true);
      setObjectsError('');
      setObjectsFeedback('');
      const response = await apiFetch(`/api/objects/${object.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, t('objects_delete_failed') || 'Failed to delete object'));
      }

      setObjectsFeedback(t('objects_delete_success') || 'Object deleted.');
      await loadObjects();
    } catch (error) {
      setObjectsError(error.message || t('objects_delete_failed') || 'Failed to delete object');
    } finally {
      setObjectsBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="dashboard">
        <h1>{t('dashboard_welcome')}</h1>

        <div className="dashboard-tabs" role="tablist" aria-label={t('dashboard_sections') || 'Dashboard sections'}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'orders'}
            className={`dashboard-tab ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            {t('dashboard_tab_orders') || 'Orders'}
          </button>
          {canManageItems && (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'items'}
              className={`dashboard-tab ${activeTab === 'items' ? 'active' : ''}`}
              onClick={() => setActiveTab('items')}
            >
              {t('dashboard_tab_items') || 'Items'}
            </button>
          )}
          {canManageObjects && (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'objects'}
              className={`dashboard-tab ${activeTab === 'objects' ? 'active' : ''}`}
              onClick={() => setActiveTab('objects')}
            >
              {t('dashboard_tab_objects') || 'Objects'}
            </button>
          )}
          {canManageItemLimits && (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'item-limits'}
              className={`dashboard-tab ${activeTab === 'item-limits' ? 'active' : ''}`}
              onClick={() => setActiveTab('item-limits')}
            >
              {t('dashboard_tab_item_limits')}
            </button>
          )}
          {adminRole && (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'admin'}
              className={`dashboard-tab ${activeTab === 'admin' ? 'active' : ''}`}
              onClick={() => setActiveTab('admin')}
            >
              {t('dashboard_tab_admin') || 'Admin Panel'}
            </button>
          )}
        </div>

        {activeTab === 'orders' && (
          <>
            <div className="filter-bar">
              <div className="field">
                <label htmlFor="beginDate"><strong>{t('begin_date') || 'Begin Date'}</strong></label>
                <input
                  id="beginDate" type="date" className="input"
                  value={beginDate} onChange={(e) => setBeginDate(e.target.value)}
                />
              </div>

              <div className="field">
                <label htmlFor="endDate"><strong>{t('end_date') || 'End Date'}</strong></label>
                <input
                  id="endDate" type="date" className="input"
                  value={endDate} onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div className="field">
                <label htmlFor="side"><strong>{t('side') || 'Side'}</strong></label>
                <select
                  id="side"
                  className="select"
                  value={side}
                  onChange={(e) => setSide(e.target.value)}
                  disabled
                >
                  <option value="receiver">{t('receiver') || 'Receiver'}</option>
                  <option value="sender">{t('sender') || 'Sender'}</option>
                </select>
              </div>

              <div className="field">
                <label htmlFor="partnerId"><strong>{t('partner_id') || 'Partner ID'}</strong></label>
                <select
                  id="partnerId" className="select"
                  value={partnerId} onChange={(e) => setPartnerId(e.target.value)}
                >
                  <option value="">{t('all_partners') || 'All partners'}</option>
                  {partners.map(p => (
                    <option key={p.company_id} value={p.company_id}>
                      {p.name ? `${p.name} (${p.company_id})` : p.company_id}
                    </option>
                  ))}
                </select>
              </div>

              <button className="btn" onClick={loadOrders}>
                {t('apply') || 'Apply'}
              </button>
            </div>
            <hr />

            <h2>{t('your_orders')}</h2>
            {message && <p>{message}</p>}

            <div style={{ marginBottom: 10 }}>
              <button className="btn" onClick={() => setShowExport(true)}>
                {t('export') || 'Export'}
              </button>
            </div>

            {orders.length > 0 ? (
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('order_id')}</th>
                    <th>{t('object_id')}</th>
                    <th>{t('object_name') || 'Object Name'}</th>
                    <th>{t('sender_company_id')}</th>
                    <th>{t('sender_company_name') || 'Sender Name'}</th>
                    <th>{t('receiver_company_id')}</th>
                    <th>{t('receiver_company_name') || 'Receiver Name'}</th>
                    <th>{t('processed')}</th>
                    <th>{t('creation_date')}</th>
                    <th>{t('comment')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, idx) => (
                    <tr key={order.order_id}>
                      <td className="col-num">{idx + 1}</td>
                      <td>{order.order_id}</td>
                      <td>{order.object_id}</td>
                      <td>{order.object_name || '-'}</td>
                      <td>{order.sender_company_id}</td>
                      <td>{order.sender_company_name || '-'}</td>
                      <td>{order.receiver_company_id}</td>
                      <td>{order.receiver_company_name || '-'}</td>
                      <td>
                        <span className={`badge ${Number(order.processed) ? 'ok' : 'no'}`}>
                          {Number(order.processed) ? t('yes_label') : t('no_label')}
                        </span>
                      </td>
                      <td>{formatDate(order.creation_date)}</td>
                      <td>{truncate(order.comment, 30)}</td>
                      <td>
                        <button className="btn" onClick={() => openOrder(order.order_id)}>
                          {t('open') || 'Open'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              !message && <p>{t('no_orders')}</p>
            )}
          </>
        )}

        {activeTab === 'items' && (
          <div className="dashboard-panel">
            <h2>{t('dashboard_tab_items') || 'Items'}</h2>

            {(itemsFeedback || itemsError) && (
              <div className="items-status-row">
                {itemsFeedback && <p className="items-success">{itemsFeedback}</p>}
                {itemsError && <p className="message">{itemsError}</p>}
              </div>
            )}

            <div className="items-layout">
              <div className="items-editor-card">
                <div className="items-editor-header">
                  <h3>{t('items_add_title') || 'Add item'}</h3>
                </div>

                <div className="items-form-grid">
                  <div className="field">
                    <label htmlFor="editItemCode"><strong>{t('item_code') || 'Item Code'}</strong></label>
                    <input
                      id="editItemCode"
                      type="text"
                      className="input"
                      value={itemForm.item_code}
                      onChange={(e) => setItemForm((prev) => ({ ...prev, item_code: e.target.value }))}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="editItemName"><strong>{t('item_name') || 'Item Name'}</strong></label>
                    <input
                      id="editItemName"
                      type="text"
                      className="input"
                      value={itemForm.item_name}
                      onChange={(e) => setItemForm((prev) => ({ ...prev, item_name: e.target.value }))}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="editBrandName"><strong>{t('brand_name') || 'Brand Name'}</strong></label>
                    <input
                      id="editBrandName"
                      type="text"
                      className="input"
                      maxLength={70}
                      value={itemForm.brand_name}
                      onChange={(e) => setItemForm((prev) => ({ ...prev, brand_name: e.target.value }))}
                    />
                  </div>
                </div>

                <button className="btn" onClick={saveItem} disabled={itemsBusy}>
                  {itemsBusy
                    ? (t('items_save_loading') || 'Saving...')
                    : (t('items_add_button') || 'Add item')}
                </button>
              </div>

              <div className="items-side-panel">
                <div className="items-filter-card">
                  <div className="items-section-head">
                    <h3>{t('filters') || 'Filters'}</h3>
                  </div>

                  <div className="items-filter-grid">
                    <div className="field">
                      <label htmlFor="itemsCode"><strong>{t('item_code') || 'Item Code'}</strong></label>
                      <input
                        id="itemsCode"
                        type="text"
                        className="input"
                        value={itemsCodeFilter}
                        onChange={(e) => setItemsCodeFilter(e.target.value)}
                      />
                    </div>

                    <div className="field">
                      <label htmlFor="itemsName"><strong>{t('item_name') || 'Item Name'}</strong></label>
                      <input
                        id="itemsName"
                        type="text"
                        className="input"
                        value={itemsNameFilter}
                        onChange={(e) => setItemsNameFilter(e.target.value)}
                      />
                    </div>
                  </div>

                  <button className="btn" onClick={loadItems} disabled={itemsListBusy}>
                    {t('apply') || 'Apply'}
                  </button>
                </div>

                <div className="items-tools">
                  <div className="items-tool-card">
                    <h3>{t('items_template_title') || 'Template'}</h3>
                    <p>{t('items_template_text') || 'Download the Excel template, fill item mappings, then upload it back here.'}</p>
                    <button className="btn" onClick={downloadItemsTemplate} disabled={templateBusy}>
                      {templateBusy
                        ? (t('items_template_loading') || 'Preparing template...')
                        : (t('items_download_template') || 'Download template')}
                    </button>
                  </div>

                  <div className="items-tool-card">
                    <h3>{t('items_export_title') || 'Download current items'}</h3>
                    <p>{t('items_export_text') || 'Download an Excel file with all item mappings where your company is the sender.'}</p>
                    <button className="btn" onClick={downloadItemsExport} disabled={itemsExportBusy}>
                      {itemsExportBusy
                        ? (t('items_export_loading') || 'Preparing file...')
                        : (t('items_download_all') || 'Download all items')}
                    </button>
                  </div>

                  <div className="items-tool-card">
                    <h3>{t('items_upload_title') || 'Upload items'}</h3>
                    <p>{t('items_upload_text') || 'Upload an Excel file with item codes and item names. Sender company is taken from your session.'}</p>
                    <input
                      key={itemsInputKey}
                      type="file"
                      accept=".xlsx,.xls"
                      className="input items-file-input"
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                        setItemsFile(file);
                        setItemsError('');
                        setItemsFeedback('');
                      }}
                    />
                    {itemsFile && (
                      <div className="items-file-name">
                        {t('items_selected_file') || 'Selected file'}: <strong>{itemsFile.name}</strong>
                      </div>
                    )}
                    <button className="btn" onClick={uploadItemsFile} disabled={itemsBusy}>
                      {itemsBusy
                        ? (t('items_upload_loading') || 'Uploading...')
                        : (t('items_upload_button') || 'Upload file')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="items-table-wrap">
              <h3>{t('items_table_title') || 'Items'}</h3>
              {itemsListBusy ? (
                <p>{t('loading') || 'Loading…'}</p>
              ) : itemsList.length > 0 ? (
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('item_code') || 'Item Code'}</th>
                      <th>{t('item_name') || 'Item Name'}</th>
                      <th>{t('brand_name') || 'Brand Name'}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsList.map((item, idx) => (
                      <tr key={item.id}>
                        <td className="col-num">{idx + 1}</td>
                        <td>{item.item_code}</td>
                        <td>{item.item_name}</td>
                        <td>{item.brand_name || '-'}</td>
                        <td className="items-actions-cell">
                          <button
                            className="btn action-btn"
                            onClick={() => startEditItem(item)}
                            title={t('edit') || 'Edit'}
                            aria-label={t('edit') || 'Edit'}
                          >
                            ✎
                          </button>
                          <button
                            className="btn ghost action-btn action-btn-danger"
                            onClick={() => deleteItem(item)}
                            title={t('delete') || 'Delete'}
                            aria-label={t('delete') || 'Delete'}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>{t('items_empty') || 'No items found.'}</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'objects' && (
          <div className="dashboard-panel">
            <h2>{t('dashboard_tab_objects') || 'Objects'}</h2>

            {(objectsFeedback || objectsError) && (
              <div className="items-status-row">
                {objectsFeedback && <p className="items-success">{objectsFeedback}</p>}
                {objectsError && <p className="message">{objectsError}</p>}
              </div>
            )}

            <div className="items-layout">
              <div className="items-editor-card">
                <div className="items-editor-header">
                  <h3>{t('objects_add_title') || 'Add object'}</h3>
                </div>

                <div className="items-form-grid">
                  <div className="field">
                    <label htmlFor="editObjectId"><strong>{t('object_id') || 'Object ID'}</strong></label>
                    <input
                      id="editObjectId"
                      type="text"
                      className="input"
                      value={objectForm.object_id}
                      onChange={(e) => setObjectForm((prev) => ({ ...prev, object_id: e.target.value }))}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="editObjectName"><strong>{t('object_name') || 'Object Name'}</strong></label>
                    <input
                      id="editObjectName"
                      type="text"
                      className="input"
                      value={objectForm.object_name}
                      onChange={(e) => setObjectForm((prev) => ({ ...prev, object_name: e.target.value }))}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="editObjectAddress"><strong>{t('object_address') || 'Object Address'}</strong></label>
                    <input
                      id="editObjectAddress"
                      type="text"
                      className="input"
                      value={objectForm.object_address}
                      onChange={(e) => setObjectForm((prev) => ({ ...prev, object_address: e.target.value }))}
                    />
                  </div>
                </div>

                <button className="btn" onClick={saveObject} disabled={objectsBusy}>
                  {objectsBusy
                    ? (t('objects_save_loading') || 'Saving...')
                    : (t('objects_add_button') || 'Add object')}
                </button>
              </div>

              <div className="items-side-panel">
                <div className="items-filter-card">
                  <div className="items-section-head">
                    <h3>{t('filters') || 'Filters'}</h3>
                  </div>

                  <div className="items-filter-grid">
                    <div className="field">
                      <label htmlFor="objectsId"><strong>{t('object_id') || 'Object ID'}</strong></label>
                      <input
                        id="objectsId"
                        type="text"
                        className="input"
                        value={objectsIdFilter}
                        onChange={(e) => setObjectsIdFilter(e.target.value)}
                      />
                    </div>

                    <div className="field">
                      <label htmlFor="objectsName"><strong>{t('object_name') || 'Object Name'}</strong></label>
                      <input
                        id="objectsName"
                        type="text"
                        className="input"
                        value={objectsNameFilter}
                        onChange={(e) => setObjectsNameFilter(e.target.value)}
                      />
                    </div>
                  </div>

                  <button className="btn" onClick={loadObjects} disabled={objectsListBusy}>
                    {t('apply') || 'Apply'}
                  </button>
                </div>

                <div className="items-tools">
                  <div className="items-tool-card">
                    <h3>{t('objects_template_title') || 'Template'}</h3>
                    <p>{t('objects_template_text') || 'Download the Excel template, fill objects, then upload it back here.'}</p>
                    <button className="btn" onClick={downloadObjectsTemplate} disabled={objectsTemplateBusy}>
                      {objectsTemplateBusy
                        ? (t('objects_template_loading') || 'Preparing template...')
                        : (t('objects_download_template') || 'Download template')}
                    </button>
                  </div>

                  <div className="items-tool-card">
                    <h3>{t('objects_export_title') || 'Download current objects'}</h3>
                    <p>{t('objects_export_text') || 'Download an Excel file with all object mappings where your company is the sender.'}</p>
                    <button className="btn" onClick={downloadObjectsExport} disabled={objectsExportBusy}>
                      {objectsExportBusy
                        ? (t('objects_export_loading') || 'Preparing file...')
                        : (t('objects_download_all') || 'Download all objects')}
                    </button>
                  </div>

                  <div className="items-tool-card">
                    <h3>{t('objects_upload_title') || 'Upload objects'}</h3>
                    <p>{t('objects_upload_text') || 'Upload an Excel file with object IDs, names and addresses. Sender company is taken from your session.'}</p>
                    <input
                      key={objectsInputKey}
                      type="file"
                      accept=".xlsx,.xls"
                      className="input items-file-input"
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                        setObjectsFile(file);
                        setObjectsError('');
                        setObjectsFeedback('');
                      }}
                    />
                    {objectsFile && (
                      <div className="items-file-name">
                        {t('objects_selected_file') || 'Selected file'}: <strong>{objectsFile.name}</strong>
                      </div>
                    )}
                    <button className="btn" onClick={uploadObjectsFile} disabled={objectsBusy}>
                      {objectsBusy
                        ? (t('objects_upload_loading') || 'Uploading...')
                        : (t('objects_upload_button') || 'Upload file')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="items-table-wrap">
              <h3>{t('objects_table_title') || 'Objects'}</h3>
              {objectsListBusy ? (
                <p>{t('loading') || 'Loading…'}</p>
              ) : objectsList.length > 0 ? (
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('object_id') || 'Object ID'}</th>
                      <th>{t('object_name') || 'Object Name'}</th>
                      <th>{t('object_address') || 'Object Address'}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {objectsList.map((object, idx) => (
                      <tr key={object.id}>
                        <td className="col-num">{idx + 1}</td>
                        <td>{object.object_id}</td>
                        <td>{object.object_name}</td>
                        <td>{object.object_address}</td>
                        <td className="items-actions-cell">
                          <button
                            className="btn action-btn"
                            onClick={() => startEditObject(object)}
                            title={t('edit') || 'Edit'}
                            aria-label={t('edit') || 'Edit'}
                          >
                            ✎
                          </button>
                          <button
                            className="btn ghost action-btn action-btn-danger"
                            onClick={() => deleteObject(object)}
                            title={t('delete') || 'Delete'}
                            aria-label={t('delete') || 'Delete'}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>{t('objects_empty') || 'No objects found.'}</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'item-limits' && canManageItemLimits && (
          <ItemLimits key={`${effectiveCompanyId}:${effectiveSide}`} companyId={effectiveCompanyId} authHeaders={authHeaders} apiFetch={apiFetch} />
        )}

        {activeTab === 'admin' && adminRole && (
          <div className="dashboard-panel">
            <h2>{t('dashboard_tab_admin') || 'Admin Panel'}</h2>

            <div className="dashboard-tabs" role="tablist" aria-label={t('admin_sections') || 'Admin sections'}>
              <button
                type="button"
                role="tab"
                aria-selected={adminPanelTab === 'session'}
                className={`dashboard-tab ${adminPanelTab === 'session' ? 'active' : ''}`}
                onClick={() => setAdminPanelTab('session')}
              >
                {t('admin_session_tab') || 'Session Parameters'}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={adminPanelTab === 'management'}
                className={`dashboard-tab ${adminPanelTab === 'management' ? 'active' : ''}`}
                onClick={() => setAdminPanelTab('management')}
              >
                {t('admin_management_tab') || 'Users & Companies Management'}
              </button>
            </div>

            {adminPanelTab === 'session' && (
              <div className="feature" style={{ marginTop: 16 }}>
                <h3>{t('admin_session_title') || 'Effective Session Context'}</h3>
                <p>{t('admin_session_text') || 'Choose which company and side the dashboard should use for this admin session.'}</p>

                {adminPanelError && <p className="message">{adminPanelError}</p>}

                <div className="items-form-grid">
                  <div className="field">
                    <label htmlFor="adminCompanyId"><strong>{t('company_id') || 'Company ID'}</strong></label>
                    <select
                      id="adminCompanyId"
                      className="select"
                      value={adminDraftCompanyId}
                      onChange={(e) => setAdminDraftCompanyId(e.target.value)}
                      disabled={adminCompaniesBusy}
                    >
                      <option value="">{t('select_company') || 'Select company'}</option>
                      {adminCompanies.map((company) => (
                        <option key={company.company_id} value={company.company_id}>
                          {company.name ? `${company.name} (${company.company_id})` : company.company_id}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="field">
                    <label htmlFor="adminSide"><strong>{t('side') || 'Side'}</strong></label>
                    <select
                      id="adminSide"
                      className="select"
                      value={adminDraftSide}
                      onChange={(e) => setAdminDraftSide(e.target.value)}
                    >
                      <option value="receiver">{t('receiver') || 'Receiver'}</option>
                      <option value="sender">{t('sender') || 'Sender'}</option>
                    </select>
                  </div>
                </div>

                <div className="items-helper-text" style={{ marginBottom: 12 }}>
                  {t('admin_session_current') || 'Current effective session'}: {effectiveCompanyId || '-'} / {effectiveSide}
                </div>

                <div className="cta-row">
                  <button className="btn" onClick={applyAdminContext} disabled={!adminDraftCompanyId}>
                    {t('apply') || 'Apply'}
                  </button>
                  <button className="btn ghost" onClick={resetAdminContext}>
                    {t('reset') || 'Reset'}
                  </button>
                </div>
              </div>
            )}

            {adminPanelTab === 'management' && (
              <div className="admin-management">
                <div className="items-editor-header">
                  <div>
                    <h3>{t('admin_management_title') || 'Users & Companies Management'}</h3>
                    <p className="items-helper-text">
                      {t('admin_management_text') || 'Manage users, companies, and their table connections.'}
                    </p>
                  </div>
                  <button
                    className="btn"
                    onClick={() => {
                      setAdminOnboardOpen(true);
                      setAdminManagementError('');
                      setAdminManagementMessage('');
                    }}
                  >
                    {t('admin_onboard_open') || 'Create company + user'}
                  </button>
                </div>

                {(adminManagementMessage || adminManagementError || adminPasswordCopyMessage) && (
                  <div className="items-status-row">
                    {adminManagementMessage && <p className="items-success">{adminManagementMessage}</p>}
                    {adminPasswordCopyMessage && <p className="items-success">{adminPasswordCopyMessage}</p>}
                    {adminManagementError && <p className="message">{adminManagementError}</p>}
                  </div>
                )}

                <div className="dashboard-tabs compact-tabs" role="tablist" aria-label="Management sections">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={adminManagementTab === 'users'}
                    className={`dashboard-tab ${adminManagementTab === 'users' ? 'active' : ''}`}
                    onClick={() => setAdminManagementTab('users')}
                  >
                    {t('admin_users_tab') || 'Users'}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={adminManagementTab === 'companies'}
                    className={`dashboard-tab ${adminManagementTab === 'companies' ? 'active' : ''}`}
                    onClick={() => setAdminManagementTab('companies')}
                  >
                    {t('admin_companies_tab') || 'Companies'}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={adminManagementTab === 'connections'}
                    className={`dashboard-tab ${adminManagementTab === 'connections' ? 'active' : ''}`}
                    onClick={() => setAdminManagementTab('connections')}
                  >
                    {t('admin_user_companies_tab') || 'Users and Companies'}
                  </button>
                </div>

                {adminManagementTab === 'users' && (
                  <>
                    <div className="items-editor-card">
                      <div className="items-editor-header">
                        <h3>{adminUserForm.id ? 'Edit user' : 'Create user'}</h3>
                        {adminUserForm.id && (
                          <button className="btn ghost" onClick={() => setAdminUserForm(emptyAdminUserForm)}>
                            {t('cancel') || 'Cancel'}
                          </button>
                        )}
                      </div>
                      <div className="admin-form-grid">
                        <div className="field">
                          <label htmlFor="adminUserUsername"><strong>Username</strong></label>
                          <input id="adminUserUsername" className="input" value={adminUserForm.username} onChange={(e) => setAdminUserForm((prev) => ({ ...prev, username: e.target.value }))} />
                        </div>
                        <div className="field">
                          <label htmlFor="adminUserEmail"><strong>Email</strong></label>
                          <input id="adminUserEmail" type="email" className="input" value={adminUserForm.email} onChange={(e) => setAdminUserForm((prev) => ({ ...prev, email: e.target.value }))} />
                        </div>
                        <div className="field">
                          <label htmlFor="adminUserPassword"><strong>{adminUserForm.id ? 'New password' : 'Password'}</strong></label>
                          <div className="admin-password-row">
                            <input id="adminUserPassword" type="text" className="input" value={adminUserForm.password} onChange={(e) => setAdminUserForm((prev) => ({ ...prev, password: e.target.value }))} />
                            <button type="button" className="btn ghost" onClick={() => generateAdminUserPassword('user')}>Generate</button>
                            <button type="button" className="btn ghost" onClick={() => copyAdminPassword(adminUserForm.password)} disabled={!adminUserForm.password}>Copy</button>
                          </div>
                        </div>
                        <div className="field">
                          <label htmlFor="adminUserSide"><strong>Side</strong></label>
                          <select id="adminUserSide" className="select" value={adminUserForm.side} onChange={(e) => setAdminUserForm((prev) => ({ ...prev, side: e.target.value }))}>
                            <option value="receiver">{t('receiver') || 'Receiver'}</option>
                            <option value="sender">{t('sender') || 'Sender'}</option>
                          </select>
                        </div>
                        <div className="field">
                          <label htmlFor="adminUserFirstName"><strong>First name</strong></label>
                          <input id="adminUserFirstName" className="input" value={adminUserForm.first_name} onChange={(e) => setAdminUserForm((prev) => ({ ...prev, first_name: e.target.value }))} />
                        </div>
                        <div className="field">
                          <label htmlFor="adminUserLastName"><strong>Last name</strong></label>
                          <input id="adminUserLastName" className="input" value={adminUserForm.last_name} onChange={(e) => setAdminUserForm((prev) => ({ ...prev, last_name: e.target.value }))} />
                        </div>
                        <label className="admin-check">
                          <input type="checkbox" checked={adminUserForm.is_admin} onChange={(e) => setAdminUserForm((prev) => ({ ...prev, is_admin: e.target.checked }))} />
                          <span>Admin user</span>
                        </label>
                      </div>
                      <button className="btn" onClick={saveAdminUser} disabled={adminManagementBusy}>
                        {adminManagementBusy ? 'Saving...' : (adminUserForm.id ? 'Update user' : 'Create user')}
                      </button>
                    </div>

                    <div className="items-table-wrap">
                      <div className="items-editor-header">
                        <h3>Users</h3>
                        <div className="admin-search-row">
                          <input className="input" placeholder="Search users" value={adminUserSearch} onChange={(e) => setAdminUserSearch(e.target.value)} />
                          <button className="btn" onClick={loadAdminUsers} disabled={adminManagementBusy}>{t('apply') || 'Apply'}</button>
                        </div>
                      </div>
                      <table className="orders-table">
                        <thead>
                          <tr>
                            <th>ID</th><th>Username</th><th>Email</th><th>Name</th><th>Side</th><th>Admin</th><th>Joined</th><th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminUsers.map((user) => (
                            <tr key={user.id}>
                              <td>{user.id}</td>
                              <td>{user.username}</td>
                              <td>{user.email}</td>
                              <td>{[user.first_name, user.last_name].filter(Boolean).join(' ') || '-'}</td>
                              <td>{user.side}</td>
                              <td>{user.is_admin ? (t('yes_label') || 'Yes') : (t('no_label') || 'No')}</td>
                              <td>{formatDate(user.date_joined)}</td>
                              <td className="items-actions-cell">
                                <button className="btn action-btn" onClick={() => startEditAdminUser(user)} title="Edit" aria-label="Edit">✎</button>
                                <button className="btn ghost action-btn action-btn-danger" onClick={() => deleteAdminUser(user)} title="Delete" aria-label="Delete">✕</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {!adminUsers.length && <p>No users found.</p>}
                    </div>
                  </>
                )}

                {adminManagementTab === 'companies' && (
                  <>
                    <div className="items-editor-card">
                      <div className="items-editor-header">
                        <h3>{adminCompanyForm.id ? 'Edit company' : 'Create company'}</h3>
                        {adminCompanyForm.id && (
                          <button className="btn ghost" onClick={() => setAdminCompanyForm(emptyAdminCompanyForm)}>
                            {t('cancel') || 'Cancel'}
                          </button>
                        )}
                      </div>
                      <div className="admin-form-grid">
                        <div className="field">
                          <label htmlFor="adminCompanyCode"><strong>Company ID</strong></label>
                          <input id="adminCompanyCode" className="input" value={adminCompanyForm.company_id} onChange={(e) => setAdminCompanyForm((prev) => ({ ...prev, company_id: e.target.value }))} />
                        </div>
                        <div className="field">
                          <label htmlFor="adminCompanyName"><strong>Name</strong></label>
                          <input id="adminCompanyName" className="input" value={adminCompanyForm.name} onChange={(e) => setAdminCompanyForm((prev) => ({ ...prev, name: e.target.value }))} />
                        </div>
                        <div className="field">
                          <label htmlFor="adminCompanyEmail"><strong>Email</strong></label>
                          <input id="adminCompanyEmail" type="email" className="input" value={adminCompanyForm.email} onChange={(e) => setAdminCompanyForm((prev) => ({ ...prev, email: e.target.value }))} />
                        </div>
                        <div className="field">
                          <label htmlFor="adminCompanyPhone"><strong>Phone</strong></label>
                          <input id="adminCompanyPhone" className="input" value={adminCompanyForm.phone_number} onChange={(e) => setAdminCompanyForm((prev) => ({ ...prev, phone_number: e.target.value }))} />
                        </div>
                        <div className="field admin-wide-field">
                          <label htmlFor="adminCompanyAddress"><strong>Address</strong></label>
                          <input id="adminCompanyAddress" className="input" value={adminCompanyForm.address} onChange={(e) => setAdminCompanyForm((prev) => ({ ...prev, address: e.target.value }))} />
                        </div>
                      </div>
                      <button className="btn" onClick={saveAdminCompany} disabled={adminManagementBusy}>
                        {adminManagementBusy ? 'Saving...' : (adminCompanyForm.id ? 'Update company' : 'Create company')}
                      </button>
                    </div>

                    <div className="items-table-wrap">
                      <div className="items-editor-header">
                        <h3>Companies</h3>
                        <div className="admin-search-row">
                          <input className="input" placeholder="Search companies" value={adminCompanySearch} onChange={(e) => setAdminCompanySearch(e.target.value)} />
                          <button className="btn" onClick={loadAdminCompaniesFull} disabled={adminManagementBusy}>{t('apply') || 'Apply'}</button>
                        </div>
                      </div>
                      <table className="orders-table">
                        <thead>
                          <tr>
                            <th>ID</th><th>Company ID</th><th>Name</th><th>Email</th><th>Phone</th><th>Address</th><th>Joined</th><th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminCompaniesFull.map((company) => (
                            <tr key={company.id}>
                              <td>{company.id}</td>
                              <td>{company.company_id}</td>
                              <td>{company.name || '-'}</td>
                              <td>{company.email || '-'}</td>
                              <td>{company.phone_number || '-'}</td>
                              <td>{company.address || '-'}</td>
                              <td>{formatDate(company.date_joined)}</td>
                              <td className="items-actions-cell">
                                <button className="btn action-btn" onClick={() => startEditAdminCompany(company)} title="Edit" aria-label="Edit">✎</button>
                                <button className="btn ghost action-btn action-btn-danger" onClick={() => deleteAdminCompany(company)} title="Delete" aria-label="Delete">✕</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {!adminCompaniesFull.length && <p>No companies found.</p>}
                    </div>
                  </>
                )}

                {adminManagementTab === 'connections' && (
                  <>
                    <div className="items-editor-card">
                      <div className="items-editor-header">
                        <h3>{adminConnectionForm.original_user_id ? 'Edit user/company connection' : 'Create user/company connection'}</h3>
                        {adminConnectionForm.original_user_id && (
                          <button className="btn ghost" onClick={() => setAdminConnectionForm(emptyAdminConnectionForm)}>
                            {t('cancel') || 'Cancel'}
                          </button>
                        )}
                      </div>
                      <div className="admin-form-grid">
                        <div className="field">
                          <label htmlFor="adminConnectionUser"><strong>User</strong></label>
                          <select id="adminConnectionUser" className="select" value={adminConnectionForm.user_id} onChange={(e) => setAdminConnectionForm((prev) => ({ ...prev, user_id: e.target.value }))}>
                            <option value="">Select user</option>
                            {adminUsers.map((user) => (
                              <option key={user.id} value={user.id}>{user.username} ({user.id})</option>
                            ))}
                          </select>
                        </div>
                        <div className="field">
                          <label htmlFor="adminConnectionCompany"><strong>Company</strong></label>
                          <select id="adminConnectionCompany" className="select" value={adminConnectionForm.company_id} onChange={(e) => setAdminConnectionForm((prev) => ({ ...prev, company_id: e.target.value }))}>
                            <option value="">Select company</option>
                            {adminCompanies.map((company) => (
                              <option key={company.company_id} value={company.company_id}>
                                {company.name ? `${company.name} (${company.company_id})` : company.company_id}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <button className="btn" onClick={saveAdminConnection} disabled={adminManagementBusy}>
                        {adminManagementBusy ? 'Saving...' : (adminConnectionForm.original_user_id ? 'Update connection' : 'Create connection')}
                      </button>
                    </div>

                    <div className="items-table-wrap">
                      <div className="items-editor-header">
                        <h3>Users and Companies</h3>
                        <div className="admin-search-row">
                          <input className="input" placeholder="Search connections" value={adminConnectionSearch} onChange={(e) => setAdminConnectionSearch(e.target.value)} />
                          <button className="btn" onClick={loadAdminConnections} disabled={adminManagementBusy}>{t('apply') || 'Apply'}</button>
                        </div>
                      </div>
                      <table className="orders-table">
                        <thead>
                          <tr>
                            <th>User ID</th><th>Username</th><th>User Email</th><th>Company ID</th><th>Company Name</th><th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {adminConnections.map((connection) => (
                            <tr key={`${connection.user_id}-${connection.company_id}`}>
                              <td>{connection.user_id}</td>
                              <td>{connection.username || '-'}</td>
                              <td>{connection.user_email || '-'}</td>
                              <td>{connection.company_id}</td>
                              <td>{connection.company_name || '-'}</td>
                              <td className="items-actions-cell">
                                <button className="btn action-btn" onClick={() => startEditAdminConnection(connection)} title="Edit" aria-label="Edit">✎</button>
                                <button className="btn ghost action-btn action-btn-danger" onClick={() => deleteAdminConnection(connection)} title="Delete" aria-label="Delete">✕</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {!adminConnections.length && <p>No user/company connections found.</p>}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {adminOnboardOpen && (
          <div className="modal-backdrop" onClick={() => setAdminOnboardOpen(false)}>
            <div className="modal-card admin-onboard-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <strong>Create company, user and connection</strong>
                <button className="modal-close" onClick={() => setAdminOnboardOpen(false)}>×</button>
              </div>
              <div className="modal-body">
                <h3>Company</h3>
                <div className="admin-form-grid">
                  <div className="field">
                    <label htmlFor="onboardCompanyId"><strong>Company ID</strong></label>
                    <input id="onboardCompanyId" className="input" value={adminOnboardForm.company.company_id} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, company: { ...prev.company, company_id: e.target.value } }))} />
                  </div>
                  <div className="field">
                    <label htmlFor="onboardCompanyName"><strong>Name</strong></label>
                    <input id="onboardCompanyName" className="input" value={adminOnboardForm.company.name} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, company: { ...prev.company, name: e.target.value } }))} />
                  </div>
                  <div className="field">
                    <label htmlFor="onboardCompanyEmail"><strong>Email</strong></label>
                    <input id="onboardCompanyEmail" type="email" className="input" value={adminOnboardForm.company.email} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, company: { ...prev.company, email: e.target.value } }))} />
                  </div>
                  <div className="field">
                    <label htmlFor="onboardCompanyPhone"><strong>Phone</strong></label>
                    <input id="onboardCompanyPhone" className="input" value={adminOnboardForm.company.phone_number} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, company: { ...prev.company, phone_number: e.target.value } }))} />
                  </div>
                  <div className="field admin-wide-field">
                    <label htmlFor="onboardCompanyAddress"><strong>Address</strong></label>
                    <input id="onboardCompanyAddress" className="input" value={adminOnboardForm.company.address} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, company: { ...prev.company, address: e.target.value } }))} />
                  </div>
                </div>

                <h3>User</h3>
                <div className="admin-form-grid">
                  <div className="field">
                    <label htmlFor="onboardUsername"><strong>Username</strong></label>
                    <input id="onboardUsername" className="input" value={adminOnboardForm.user.username} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, user: { ...prev.user, username: e.target.value } }))} />
                  </div>
	                  <div className="field">
	                    <label htmlFor="onboardUserEmail"><strong>Email</strong></label>
	                    <input id="onboardUserEmail" type="email" className="input" value={adminOnboardForm.user.email} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, user: { ...prev.user, email: e.target.value } }))} />
	                  </div>
                    <label className="admin-check">
                      <input
                        type="checkbox"
                        checked={adminOnboardForm.user.api_dash}
                        onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, user: { ...prev.user, api_dash: e.target.checked } }))}
                      />
                      <span>api + dash</span>
                    </label>
                    {!adminOnboardForm.user.api_dash && (
                      <>
                        <div className="field">
                          <label htmlFor="onboardPassword"><strong>Password</strong></label>
                          <div className="admin-password-row">
                            <input id="onboardPassword" type="text" className="input" value={adminOnboardForm.user.password} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, user: { ...prev.user, password: e.target.value } }))} />
                            <button type="button" className="btn ghost" onClick={() => generateAdminUserPassword('onboard')}>Generate</button>
                            <button type="button" className="btn ghost" onClick={() => copyAdminPassword(adminOnboardForm.user.password)} disabled={!adminOnboardForm.user.password}>Copy</button>
                          </div>
                        </div>
                        <div className="field">
                          <label htmlFor="onboardAuthKey"><strong>App key</strong></label>
                          <div className="admin-password-row">
                            <input
                              id="onboardAuthKey"
                              type="text"
                              className="input"
                              readOnly
                              value={makeAuthKey(adminOnboardForm.user.username.trim(), adminOnboardForm.user.password)}
                            />
                            <button
                              type="button"
                              className="btn ghost"
                              onClick={() => copyAdminPassword(makeAuthKey(adminOnboardForm.user.username.trim(), adminOnboardForm.user.password))}
                              disabled={!makeAuthKey(adminOnboardForm.user.username.trim(), adminOnboardForm.user.password)}
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                    {adminOnboardForm.user.api_dash && (
                      <>
                        <div className="field admin-wide-field">
                          <div className="items-helper-text">
                            Users to create: api_{adminOnboardForm.user.username || 'username'} and dash_{adminOnboardForm.user.username || 'username'}
                          </div>
                        </div>
                        <div className="field">
                          <label htmlFor="onboardApiPassword"><strong>API password</strong></label>
                          <div className="admin-password-row">
                            <input id="onboardApiPassword" type="text" className="input" value={adminOnboardForm.user.api_password} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, user: { ...prev.user, api_password: e.target.value } }))} />
                            <button type="button" className="btn ghost" onClick={() => generateAdminUserPassword('onboardApi')}>Generate</button>
                            <button type="button" className="btn ghost" onClick={() => copyAdminPassword(adminOnboardForm.user.api_password)} disabled={!adminOnboardForm.user.api_password}>Copy</button>
                          </div>
                        </div>
                        <div className="field">
                          <label htmlFor="onboardApiAuthKey"><strong>API app key</strong></label>
                          <div className="admin-password-row">
                            <input
                              id="onboardApiAuthKey"
                              type="text"
                              className="input"
                              readOnly
                              value={makeAuthKey(`api_${adminOnboardForm.user.username.trim()}`, adminOnboardForm.user.api_password)}
                            />
                            <button
                              type="button"
                              className="btn ghost"
                              onClick={() => copyAdminPassword(makeAuthKey(`api_${adminOnboardForm.user.username.trim()}`, adminOnboardForm.user.api_password))}
                              disabled={!makeAuthKey(`api_${adminOnboardForm.user.username.trim()}`, adminOnboardForm.user.api_password)}
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                        <div className="field">
                          <label htmlFor="onboardDashPassword"><strong>Dash password</strong></label>
                          <div className="admin-password-row">
                            <input id="onboardDashPassword" type="text" className="input" value={adminOnboardForm.user.dash_password} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, user: { ...prev.user, dash_password: e.target.value } }))} />
                            <button type="button" className="btn ghost" onClick={() => generateAdminUserPassword('onboardDash')}>Generate</button>
                            <button type="button" className="btn ghost" onClick={() => copyAdminPassword(adminOnboardForm.user.dash_password)} disabled={!adminOnboardForm.user.dash_password}>Copy</button>
                          </div>
                        </div>
                        <div className="field">
                          <label htmlFor="onboardDashAuthKey"><strong>Dash app key</strong></label>
                          <div className="admin-password-row">
                            <input
                              id="onboardDashAuthKey"
                              type="text"
                              className="input"
                              readOnly
                              value={makeAuthKey(`dash_${adminOnboardForm.user.username.trim()}`, adminOnboardForm.user.dash_password)}
                            />
                            <button
                              type="button"
                              className="btn ghost"
                              onClick={() => copyAdminPassword(makeAuthKey(`dash_${adminOnboardForm.user.username.trim()}`, adminOnboardForm.user.dash_password))}
                              disabled={!makeAuthKey(`dash_${adminOnboardForm.user.username.trim()}`, adminOnboardForm.user.dash_password)}
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                      </>
                    )}
	                  <div className="field">
	                    <label htmlFor="onboardSide"><strong>Side</strong></label>
                    <select id="onboardSide" className="select" value={adminOnboardForm.user.side} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, user: { ...prev.user, side: e.target.value } }))}>
                      <option value="receiver">{t('receiver') || 'Receiver'}</option>
                      <option value="sender">{t('sender') || 'Sender'}</option>
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor="onboardFirstName"><strong>First name</strong></label>
                    <input id="onboardFirstName" className="input" value={adminOnboardForm.user.first_name} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, user: { ...prev.user, first_name: e.target.value } }))} />
                  </div>
                  <div className="field">
                    <label htmlFor="onboardLastName"><strong>Last name</strong></label>
                    <input id="onboardLastName" className="input" value={adminOnboardForm.user.last_name} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, user: { ...prev.user, last_name: e.target.value } }))} />
                  </div>
                  <label className="admin-check">
                    <input type="checkbox" checked={adminOnboardForm.user.is_admin} onChange={(e) => setAdminOnboardForm((prev) => ({ ...prev, user: { ...prev.user, is_admin: e.target.checked } }))} />
                    <span>Admin user</span>
                  </label>
                </div>

                {adminPasswordCopyMessage && <p className="items-success">{adminPasswordCopyMessage}</p>}
                {adminManagementError && <p className="message">{adminManagementError}</p>}

                <div className="items-modal-actions">
                  <button className="btn" onClick={createAdminOnboarding} disabled={adminManagementBusy}>
                    {adminManagementBusy ? 'Creating...' : 'Create all'}
                  </button>
                  <button className="btn ghost" onClick={() => setAdminOnboardOpen(false)}>
                    {t('cancel') || 'Cancel'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {openId && (
          <div className="modal-backdrop" onClick={closeOrder}>
            <div className="modal-card" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <strong>{t('order')} #{openId}</strong>
                  {detail && (
                    <span style={{ marginLeft: 10, color: '#666' }}>
                      • {t('sender_company_id')}: {detail.sender_company_id}
                      {' '}({detail.sender_company_name || '-'})
                      {'  '}• {t('receiver_company_id')}: {detail.receiver_company_id}
                      {' '}({detail.receiver_company_name || '-'})
                    </span>
                  )}
                </div>
                <button className="modal-close" onClick={closeOrder}>×</button>
              </div>
              <div className="modal-body">
                {loadingDetail && <p>{t('loading') || 'Loading…'}</p>}
                {detailErr && <p className="message">{detailErr}</p>}
                {detail && (
                  <>
                    <div style={{ marginBottom: 10 }}>
                      <div><b>{t('object_id') || 'Object ID'}:</b> {detail.object_id}</div>
                      <div><b>{t('object_name') || 'Object Name'}:</b> {detail.object_name || '-'}</div>
                      <div><b>{t('object_address') || 'Object Address'}:</b> {detail.object_address || '-'}</div>
                      <div><b>{t('sender_company_name') || 'Sender Name'}:</b> {detail.sender_company_name || '-'}</div>
                      <div><b>{t('receiver_company_name') || 'Receiver Name'}:</b> {detail.receiver_company_name || '-'}</div>
                      <div><b>{t('processed') || 'Processed'}:</b> {detail.processed ? t('yes_label') : t('no_label')}</div>
                      <div><b>{t('creation_date') || 'Creation'}:</b> {formatDate(detail.creation_date)}</div>
                      {detail.comment && <div><b>{t('comment')}:</b> {detail.comment}</div>}
                    </div>
                    <h3 style={{ marginTop: 10 }}>{t('items') || 'Items'}</h3>
                    <div className="items-scroll">
                      <table className="items-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>{t('barcode') || 'Barcode'}</th>
                            <th>{t('item_name') || 'Item Name'}</th>
                            <th>{t('brand_name') || 'Brand Name'}</th>
                            <th>{t('qty') || 'Qty'}</th>
                            {side !== 'receiver' && <th>{t('item_limits.limit')}</th>}
                            <th>{t('unit') || 'Unit'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.items.map((it, idx) => (
                            <tr key={it.id}>
                              <td className="col-num">{idx + 1}</td>
                              <td>{it.barcode}</td>
                              <td>{it.item_name || '-'}</td>
                              <td>{it.brand_name || '-'}</td>
                              <td>{it.qty}</td>
                              {side !== 'receiver' && <td>{it.limit ?? ''}</td>}
                              <td>{it.unit}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {editItemOpen && (
          <div className="modal-backdrop" onClick={closeEditItem}>
            <div className="modal-card items-edit-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <strong>{t('items_edit_title') || 'Edit item'}</strong>
                <button className="modal-close" onClick={closeEditItem}>×</button>
              </div>
              <div className="modal-body">
                <div className="items-form-grid">
                  <div className="field">
                    <label htmlFor="modalItemCode"><strong>{t('item_code') || 'Item Code'}</strong></label>
                    <input
                      id="modalItemCode"
                      type="text"
                      className="input"
                      value={editItemForm.item_code}
                      onChange={(e) => setEditItemForm((prev) => ({ ...prev, item_code: e.target.value }))}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="modalItemName"><strong>{t('item_name') || 'Item Name'}</strong></label>
                    <input
                      id="modalItemName"
                      type="text"
                      className="input"
                      value={editItemForm.item_name}
                      onChange={(e) => setEditItemForm((prev) => ({ ...prev, item_name: e.target.value }))}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="modalBrandName"><strong>{t('brand_name') || 'Brand Name'}</strong></label>
                    <input
                      id="modalBrandName"
                      type="text"
                      className="input"
                      maxLength={70}
                      value={editItemForm.brand_name}
                      onChange={(e) => setEditItemForm((prev) => ({ ...prev, brand_name: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="items-modal-actions">
                  <button className="btn" onClick={updateItem} disabled={itemsBusy}>
                    {itemsBusy
                      ? (t('items_save_loading') || 'Saving...')
                      : (t('items_update_button') || 'Update item')}
                  </button>
                  <button className="btn ghost" onClick={closeEditItem}>
                    {t('close') || 'Close'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {editObjectOpen && (
          <div className="modal-backdrop" onClick={closeEditObject}>
            <div className="modal-card items-edit-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <strong>{t('objects_edit_title') || 'Edit object'}</strong>
                <button className="modal-close" onClick={closeEditObject}>×</button>
              </div>
              <div className="modal-body">
                <div className="items-form-grid">
                  <div className="field">
                    <label htmlFor="modalObjectId"><strong>{t('object_id') || 'Object ID'}</strong></label>
                    <input
                      id="modalObjectId"
                      type="text"
                      className="input"
                      value={editObjectForm.object_id}
                      onChange={(e) => setEditObjectForm((prev) => ({ ...prev, object_id: e.target.value }))}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="modalObjectName"><strong>{t('object_name') || 'Object Name'}</strong></label>
                    <input
                      id="modalObjectName"
                      type="text"
                      className="input"
                      value={editObjectForm.object_name}
                      onChange={(e) => setEditObjectForm((prev) => ({ ...prev, object_name: e.target.value }))}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="modalObjectAddress"><strong>{t('object_address') || 'Object Address'}</strong></label>
                    <input
                      id="modalObjectAddress"
                      type="text"
                      className="input"
                      value={editObjectForm.object_address}
                      onChange={(e) => setEditObjectForm((prev) => ({ ...prev, object_address: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="items-modal-actions">
                  <button className="btn" onClick={updateObject} disabled={objectsBusy}>
                    {objectsBusy
                      ? (t('objects_save_loading') || 'Saving...')
                      : (t('objects_update_button') || 'Update object')}
                  </button>
                  <button className="btn ghost" onClick={closeEditObject}>
                    {t('close') || 'Close'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showExport && (
          <div className="modal-backdrop" onClick={() => setShowExport(false)}>
            <div className="modal-card" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <strong>{t('export_format') || 'Choose export format'}</strong>
                <button className="modal-close" onClick={() => setShowExport(false)}>×</button>
              </div>
              <div className="modal-body">
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn" onClick={() => { setShowExport(false); exportOrders('csv'); }}>
                    {t('export_csv') || 'CSV'}
                  </button>
                  <button className="btn" onClick={() => { setShowExport(false); exportOrders('xlsx'); }}>
                    {t('export_xlsx') || 'XLSX'}
                  </button>
                  <button className="btn" onClick={() => { setShowExport(false); exportOrders('pdf'); }}>
                    {t('export_pdf') || 'PDF'}
                  </button>
                  <button className="btn" onClick={() => setShowExport(false)}>
                    {t('cancel') || 'Cancel'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};


/* =======================
   API Docs (public) — v1.5
======================= */
const ApiDocs = () => {
  const [tab, setTab] = React.useState('upload');

  const baseHttp  = 'http://getorders.ge:3080';
  const baseHttps = 'https://getorders.ge:3443';

  const uploadFields = [
    { name: 'object_id',           type: 'String(20)', required: '1',  desc: 'ობიექტის (კონტრაგენტის) კოდი Sharm-ის სისტემაში (გამოგზავნილი იქნება Sharm-ის მიერ ან შეთანხმებით). გაუქმებისას არ იგზავნება.' },
    { name: 'sender_company_id',   type: 'String(20)', required: '1',  desc: 'გამომგზავნი კომპანიის უნიკალური იდენტიფიკატორი. გაუქმებისას არ იგზავნება.' },
    { name: 'receiver_company_id', type: 'String(20)', required: '1',  desc: 'მიმღები კომპანიის უნიკალური იდენტიფიკატორი. გაუქმებისას არ იგზავნება.' },
    { name: 'order_id',            type: 'String(20)', required: '1',  desc: 'შეკვეთის უნიკალური კოდი შემკვეთის სისტემაში.' },
    { name: 'action',              type: 'Number',     required: '1',  desc: '0 — ახალი შეკვეთა; -1 — არსებული შეკვეთის გაუქმება' },
    { name: 'delivery_date',       type: 'Date',       required: '1',  desc: 'მიტანის თარიღი ISO ფორმატში (შეიძლება იყოს ცარიელი). გაუქმებისას არ იგზავნება.' },
    { name: 'comment',             type: 'String(150)',required: '1',  desc: 'კომენტარი (შეიძლება ცარიელი). გაუქმებისას არ იგზავნება.' },
    { name: 'products',            type: 'Array',      required: '1/0',desc: 'ახალი შეკვეთა: საჭიროა (1); გაუქმება: არ იგზავნება (0). ელემენტები: { barcode:String(20), quantity:Number, unit:String(6) }' },
  ];

  const uploadExampleNew = `{
  "object_id":"cl0000000001",
  "sender_company_id":"12345678910",
  "receiver_company_id":"11111111111111",
  "order_id":"po0000000001",
  "action": 0,
  "delivery_date": "2023-11-05",
  "comment":"კვირის ბოლომდე",
  "products":[
    {"barcode":"8012339001233", "quantity":4,    "unit":"1"},
    {"barcode":"7218312873198", "quantity":12.3, "unit":"99"}
  ]
}`;

  const uploadExampleCancel = `{
  "object_id": "cl0000000001",
  "order_id": "po0000000001",
  "action": -1
}`;

  const uploadResponseExample = `{
  "status": "0",
  "message": "Order cancelled successfully"
}`;

  const statusCodes = [
    { code:  '0',  text: 'წარმატებით გაუქმდა შეკვეთა' },
    { code: '-1',  text: 'შეკვეთა უკვე დამუშავებულია და ვერ გაუქმდება' },
    { code: '-2',  text: 'შეკვეთა უკვე გაუქმებულია' },
    { code: '-3',  text: 'შეკვეთა ვერ მოიძებნა' },
  ];

  const downloadFields = [
    { name: 'receiver_company_id', type: 'String(20)', required: '1', desc: 'მიმღები კომპანიის უნიკალური იდენტიფიკატორი' },
    { name: 'response_type',       type: 'String(4)',  required: '1', desc: '\"json\" ან \"xml\"' },
  ];

  const downloadRequestSample = `{
  "receiver_company_id": "company_1",
  "response_type": "json"
}`;

  const downloadResponseSample = `{
  "number_of_orders": 1,
  "orders": [
    {
      "object_id": "12345",
      "order_id": "ord00002",
      "receiver_company_id": "01987654321",
      "action": 0,
      "comment": "comment1",
      "creation_date": "2026-02-04T11:25:00+04:00",
      "delivery_date": "2026-02-05T00:00:00+04:00",
      "products": [
        { "barcode": "123456789012", "quantity": 3,  "unit": "pcs" },
        { "barcode": "123456789013", "quantity": 11, "unit": "pcs" }
      ]
    }
  ]
}`;
  const testUsers = [
    { user: 'satesto1', pwd: '123456',   company_id: 'company_1' },
    { user: 'satesto2', pwd: '12345678', company_id: 'company_2' },
  ];

  const curlUploadNew = `curl -s -X POST ${baseHttps}/api/upload_order \\
  -H "Content-Type: application/json" \\
  -d '${uploadExampleNew.replace(/\n/g, ' ').replace(/\s+/g,' ').trim()}'`;

  const curlUploadCancel = `curl -s -X POST ${baseHttps}/api/upload_order \\
  -H "Content-Type: application/json" \\
  -d '${uploadExampleCancel.replace(/\n/g, ' ').replace(/\s+/g,' ').trim()}'`;

  const curlDownload = `curl -s -X POST ${baseHttps}/api/download_order \\
  -H "Content-Type: application/json" \\
  -d '${downloadRequestSample.replace(/\n/g, ' ').replace(/\s+/g,' ').trim()}'`;

  const rangeFields = [
    { name: 'receiver_company_id', type: 'String(20)', required: '1', desc: 'მიმღები კომპანიის უნიკალური იდენტიფიკატორი' },
    { name: 'start_date',          type: 'Date',       required: '1', desc: 'დაწყების თარიღი ISO ფორმატში (creation_date >= start_date)' },
    { name: 'end_date',            type: 'Date',       required: '1', desc: 'დასრულების თარიღი ISO ფორმატში (creation_date <= end_date)' },
    { name: 'response_type',       type: 'String(4)',  required: '1', desc: '"json" ან "xml"' },
  ];

  const rangeRequestSample = `{
  "receiver_company_id": "company_1",
  "start_date": "2026-02-01",
  "end_date": "2026-02-04",
  "response_type": "json"
}`;

  const rangeResponseSample = `{
  "number_of_orders": 2,
  "orders": [
    {
      "object_id": "cl0000000001",
      "order_id": "po0000000001",
      "receiver_company_id": "company_1",
      "sender_company_id": "12345678910",
      "action": 0,
      "comment": "fetch",
      "creation_date": "2026-02-03T15:10:00+04:00",
      "delivery_date": "2026-02-05T00:00:00+04:00",
      "products": [
        { "barcode": "8012339001233", "quantity": 4, "unit": "1" }
      ]
    }
  ]
}`;

  const curlRange = `curl -s -X POST ${baseHttps}/api/download_orders_range \\
  -H "Content-Type: application/json" \\
  -d '${rangeRequestSample.replace(/\n/g, ' ').replace(/\s+/g,' ').trim()}'`;

  return (
    <div className="page">
      <div className="hero-card">
        <h1 style={{marginBottom:8}}>GetOrders API — v1.6</h1>
        <p style={{marginTop:0}}>ინფორმაციის მიმოცვლის ფორმატი: <b>JSON</b> (სურვილისამებრ ჩამოტვირთვა <b>XML</b>-ში).</p>
        <div className="cta-row" style={{marginTop:10}}>
          <span className="badge ok">რეკომენდაცია: გამოიყენეთ HTTPS</span>
          {/* <span className="badge">HTTP: {baseHttp}</span>
          <span className="badge">HTTPS: {baseHttps}</span> */}
        </div>
      </div>

      {/* Tabs */}
      <div className="feature-grid" style={{gridTemplateColumns:'1fr'}}>
        <div className="feature" style={{padding:0}}>
          <div style={{display:'flex'}}>
            <button className={`nav-link ${tab==='upload'?'active':''}`} onClick={()=>setTab('upload')}>post::upload_order</button>
            <button className={`nav-link ${tab==='download'?'active':''}`} onClick={()=>setTab('download')}>post::download_order</button>
            <button className={`nav-link ${tab==='range'?'active':''}`} onClick={()=>setTab('range')}>post::download_orders_range</button>
          </div>

          {/* Upload tab */}
          {tab==='upload' && (
            <div style={{padding:16}}>
              <h3 style={{marginTop:0}}>post::upload_order</h3>
              <p>ახალი შეკვეთის გაგზავნა ან არსებული შეკვეთის გაუქმება.</p>

              <h4>ველი</h4>
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>#</th><th>დასახელება</th><th>ტიპი</th><th>სავალდებულო</th><th>აღწერა</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadFields.map((f,i)=>(
                    <tr key={i}>
                      <td className="col-num">{i + 1}</td>
                      <td><code>{f.name}</code></td>
                      <td>{f.type}</td>
                      <td>{f.required}</td>
                      <td>{f.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h4 style={{marginTop:16}}>მაგალითი — ახალი შეკვეთა (action: 0)</h4>
              <pre style={{background:'#f6f8fb', padding:12, borderRadius:8, overflowX:'auto'}}><code>{uploadExampleNew}</code></pre>
              <p style={{marginTop:8}}>cURL:</p>
              <pre style={{background:'#f6f8fb', padding:12, borderRadius:8, overflowX:'auto'}}><code>{curlUploadNew}</code></pre>

              <h4 style={{marginTop:16}}>მაგალითი — გაუქმება (action: -1)</h4>
              <pre style={{background:'#f6f8fb', padding:12, borderRadius:8, overflowX:'auto'}}><code>{uploadExampleCancel}</code></pre>
              <p style={{marginTop:8}}>სერვერის პასუხი (მაგალითი):</p>
              <pre style={{background:'#f6f8fb', padding:12, borderRadius:8, overflowX:'auto'}}><code>{uploadResponseExample}</code></pre>

              <h4 style={{marginTop:16}}>სტატუსის კოდები</h4>
              <table className="orders-table">
                <thead><tr><th>კოდი</th><th>აღწერა</th></tr></thead>
                <tbody>
                  {statusCodes.map((s,i)=>(
                    <tr key={i}><td><code>{s.code}</code></td><td>{s.text}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Download tab */}
          {tab==='download' && (
            <div style={{padding:16}}>
              <h3 style={{marginTop:0}}>post::download_order</h3>
              <p>მიღება მიმღები კომპანიისთვის განკუთვნილი შეკვეთების.</p>

              <h4>საკითხავი ველები</h4>
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>#</th><th>დასახელება</th><th>ტიპი</th><th>სავალდებულო</th><th>აღწერა</th>
                  </tr>
                </thead>
                <tbody>
                  {downloadFields.map((f,i)=>(
                    <tr key={i}>
                      <td className="col-num">{i + 1}</td>
                      <td><code>{f.name}</code></td>
                      <td>{f.type}</td>
                      <td>{f.required}</td>
                      <td>{f.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h4 style={{marginTop:16}}>გასაგზავნი JSON</h4>
              <pre style={{background:'#f6f8fb', padding:12, borderRadius:8, overflowX:'auto'}}><code>{downloadRequestSample}</code></pre>

              <p style={{marginTop:8}}>პასუხის მაგალითი (response_type: "json")</p>
              <pre style={{background:'#f6f8fb', padding:12, borderRadius:8, overflowX:'auto'}}><code>{downloadResponseSample}</code></pre>

              <h4 style={{marginTop:16}}>cURL</h4>
              <pre style={{background:'#f6f8fb', padding:12, borderRadius:8, overflowX:'auto'}}><code>{curlDownload}</code></pre>
            </div>
          )}
          {/* Download range tab */}
          {tab==='range' && (
              <div style={{padding:16}}>
                <h3 style={{marginTop:0}}>post::download_orders_range</h3>
                <p>
                  შეკვეთების მიღება თარიღის შუალედით <b>(creation_date)</b>. აბრუნებს შეკვეთებს <b>მიუხედავად</b> processed/cancelled სტატუსისა
                  და <b>არ</b> ანახლებს processed ველს ბაზაში.
                </p>

                <h4>საკითხავი ველები</h4>
                <table className="orders-table">
                  <thead>
                  <tr>
                    <th>#</th><th>დასახელება</th><th>ტიპი</th><th>სავალდებულო</th><th>აღწერა</th>
                  </tr>
                  </thead>
                  <tbody>
                  {rangeFields.map((f,i)=>(
                      <tr key={i}>
                        <td className="col-num">{i + 1}</td>
                        <td><code>{f.name}</code></td>
                        <td>{f.type}</td>
                        <td>{f.required}</td>
                        <td>{f.desc}</td>
                      </tr>
                  ))}
                  </tbody>
                </table>

                <h4 style={{marginTop:16}}>გასაგზავნი JSON</h4>
                <pre style={{background:'#f6f8fb', padding:12, borderRadius:8, overflowX:'auto'}}><code>{rangeRequestSample}</code></pre>

                <p style={{marginTop:8}}>პასუხის მაგალითი (response_type: "json")</p>
                <pre style={{background:'#f6f8fb', padding:12, borderRadius:8, overflowX:'auto'}}><code>{rangeResponseSample}</code></pre>

                <h4 style={{marginTop:16}}>cURL</h4>
                <pre style={{background:'#f6f8fb', padding:12, borderRadius:8, overflowX:'auto'}}><code>{curlRange}</code></pre>
              </div>
          )}

        </div>
      </div>

      {/* <div className="feature-grid" style={{gridTemplateColumns:'1fr 1fr', marginTop:16}}>
        <div className="feature">
          <h3>სატესტო მომხმარებლები</h3>
          <ul style={{marginTop:8}}>
            {testUsers.map((u,i)=>(
              <li key={i} style={{marginBottom:6}}>
                <b>user:</b> <code>{u.user}</code> • <b>pwd:</b> <code>{u.pwd}</code> • <b>company_id:</b> <code>{u.company_id}</code>
              </li>
            ))}
          </ul>
          <p style={{marginTop:8, fontSize:13, color:'#555'}}>მონაცემების დაცვის მიზნით რეკომენდებულია <b>HTTPS</b> გამოყენება.</p>
        </div>

        <div className="feature">
          <h3>ენდპოინტები</h3>
          <ul style={{marginTop:8}}>
            <li><code>POST {baseHttps}/api/upload_order</code></li>
            <li><code>POST {baseHttps}/api/download_order</code></li>
          </ul>
        </div>
      </div> */}
    </div>
  );
};

/* =======================
   Partners (public)
======================= */
const Partners = () => {
  return (
    <div className="page">
      <div className="feature">
        <PartnerShowcase />
      </div>
    </div>
  );
};

/* =======================
   Contact (public)
======================= */
const Contact = () => {
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [status, setStatus] = React.useState(null); // {ok:bool, text:string}

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (!name || !email || !message) {
      setStatus({ ok: false, text: "Please fill in all fields." });
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message })
      });
      if (!res.ok) throw new Error(await res.text());
      setStatus({ ok: true, text: "Thanks! We received your message and will reply soon." });
      setName(""); setEmail(""); setMessage("");
    } catch (err) {
      setStatus({ ok: false, text: "Failed to send. Please try again later." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <div className="hero-card">
        <h1>Contact</h1>
        <p>Reach us for onboarding, support, or partnership inquiries.</p>
        <p><b>Email:</b> support@getorders.ge</p>
        <p><b>Phone:</b> +995 (___) ___-___</p>
        <form onSubmit={onSubmit} style={{marginTop:16, display:'grid', gap:12}}>
          <div className="field">
            <label htmlFor="cname">Name</label>
            <input id="cname" className="input" value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" />
          </div>
          <div className="field">
            <label htmlFor="cemail">Email</label>
            <input id="cemail" type="email" className="input" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" />
          </div>
          <div className="field">
            <label htmlFor="cmsg">Message</label>
            <textarea id="cmsg" className="input" style={{height:120, paddingTop:8, paddingBottom:8}}
                      value={message} onChange={e=>setMessage(e.target.value)} placeholder="How can we help?" />
          </div>
          <div className="cta-row">
            <button className="btn" type="submit" disabled={submitting}>{submitting ? "Sending…" : "Send message"}</button>
          </div>
          {status && (
            <div style={{
              marginTop:8,
              background: status.ok ? '#e8f5e9' : '#ffebee',
              color: status.ok ? '#2e7d32' : '#c62828',
              padding:'8px 12px', borderRadius:8
            }}>
              {status.text}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

/* =======================
   Partners Footer (global)
======================= */
const PartnersFooter = () => {
  return (
    <div className="page partners-footer">
      <div className="feature" style={{ padding: '12px 16px' }}>
        <PartnerShowcase compact />
      </div>
    </div>
  );
};

/* =======================
   App
======================= */
function App() {
  const { i18n } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [session, setSession] = useState(null);
  const [adminContext, setAdminContext] = useState(null);
  const [view, setView] = useState('home'); // 'home' | 'login' | 'dashboard' | 'changePassword' | 'api' | 'partners' | 'contact'

  useEffect(() => {
    const currentSession = readSessionFromToken();
    if (currentSession) {
      setSession(currentSession);
      if (currentSession.adminRole) {
        setAdminContext(readAdminContext() || { companyId: currentSession.companyId, side: currentSession.side });
      } else {
        clearAdminContext();
        setAdminContext(null);
      }
      setIsLoggedIn(true);
    }
  }, []);

  const goHome = () => setView('home');
  const goLogin = () => setView('login');
  const goDashboard = () => setView(isLoggedIn ? 'dashboard' : 'login');

  const handleLogout = () => {
    localStorage.removeItem('token');
    clearAdminContext();
    setIsLoggedIn(false);
    setSession(null);
    setAdminContext(null);
    setView('home');
  };

  const afterLogin = () => {
    const currentSession = readSessionFromToken();
    setSession(currentSession);
    if (currentSession?.adminRole) {
      const nextContext = { companyId: currentSession.companyId, side: currentSession.side };
      writeAdminContext(nextContext);
      setAdminContext(nextContext);
    } else {
      clearAdminContext();
      setAdminContext(null);
    }
    setIsLoggedIn(true);
    setView('dashboard');
  };

  const handleAdminContextChange = (nextContext) => {
    const normalized = {
      companyId: nextContext?.companyId ? String(nextContext.companyId) : session?.companyId || '',
      side: nextContext?.side === 'sender' || nextContext?.side === 'receiver'
        ? nextContext.side
        : session?.side || 'receiver',
    };
    writeAdminContext(normalized);
    setAdminContext(normalized);
  };

  const changeLanguage = (lng) => { i18n.changeLanguage(lng); };

  return (
    <>
      {/* NAVBAR */}
      <div className="navbar">
        <div className="brand" onClick={goHome}>GetOrders</div>
        <div className="nav">
          <button
            className={`nav-link ${view === 'home' ? 'active' : ''}`}
            onClick={goHome}
          >
            Home
          </button>
          <button
            className={`nav-link ${view === 'api' ? 'active' : ''}`}
            onClick={() => setView('api')}
          >
            API
          </button>
          <button
            className={`nav-link ${view === 'contact' ? 'active' : ''}`}
            onClick={() => setView('contact')}
          >
            Contacts
          </button>
          <button
            className={`nav-link ${(view === 'dashboard') ? 'active' : ''}`}
            onClick={goDashboard}
            title={isLoggedIn ? 'Open Dashboard' : 'Login to access Dashboard'}
          >
            Dashboard
          </button>
        </div>

        <div className="navbar-right">
          <div className="topbar">
            <UserMenu
              isLoggedIn={isLoggedIn}
              onLoginClick={goLogin}
              onChangePassword={() => setView('changePassword')}
              onLogout={handleLogout}
            />
            <div className="language-switcher">
              <LanguageSelector onChangeLanguage={changeLanguage}/>
            </div>
          </div>
        </div>
      </div>

      {/* VIEWS */}
      {view === 'home' && (
        <Home goDashboard={goDashboard} goLogin={goLogin} />
      )}
      {view === 'login' && (
        <LoginForm onLoginSuccess={afterLogin} />
      )}
      {view === 'dashboard' && (
        <Dashboard
          onLogout={handleLogout}
          sessionCompanyId={session?.companyId || ''}
          sessionSide={session?.side || 'receiver'}
          adminRole={session?.adminRole || false}
          adminContext={adminContext}
          onAdminContextChange={handleAdminContextChange}
        />
      )}
      {view === 'api' && (
        <ApiDocs />
      )}
      {view === 'partners' && (
        <Partners />
      )}
      {view === 'contact' && (
        <Contact />
      )}
      {view === 'changePassword' && (
        <div className="page">
          <PasswordChange onBack={() => setView('dashboard')} />
        </div>
      )}

      {/* GLOBAL PARTNERS FOOTER */}
      <PartnersFooter />
    </>
  );
}

export default App;
